import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import to from 'await-to-js'
import axios, { AxiosError } from 'axios'
import {
  searchCompaniesList,
  searchCompaniesUniqueDomain,
  searchFirstBiteCompanies,
  searchUnmatchedCompanies,
  searchFirstBiteDoors,
  searchUnmatchedDoors,
} from './search_api'
import { ActivityListResponse } from 'models/activity'
import { ActivityListResponseV2 } from 'models/activityV2'
import { AsyncJobListResponse } from 'models/asyncJobs'
import {
  CHAIN_SUMMARY_KEY,
  CHAIN_SUMMARY_REPORTS_KEY,
  DOOR_SUMMARY_KEY,
  DOOR_SUMMARY_REPORTS_KEY,
} from 'constants/tableQueryKeys'
import {
  CampaignCreate,
  Category,
  CompanyDetailsData,
  CompanyDistributor,
  CompanyDistributorCreate,
  CompanyEmailExtensionRequests,
  CompanyGPO,
  CompanyGPOCreate,
  CompanyLogo,
} from 'models/companies'
import { DealReport } from 'models/deals_reports'
import {
  ContactCreateRequest,
  ContactListResponse,
  ContactResponseDetail,
  ContactsImportResponse,
} from 'models/contacts'
import { DealDetail, ProductPrediction } from 'models/deals'
import { DealsPipeline } from 'models/deals_pipeline'
import { HospitalChainProxy } from 'models/hospital'
import { NursingHomeChainProxy } from 'models/nursinghome'
import { Menu } from 'models/menus'
import { Notification } from 'models/notifications'
import { Preferences } from 'models/preferences'
import {
  DoorProxyDetail,
  DoorProxyList,
  ReviewsPerScore,
} from 'models/restaurant'
import {
  FilterIdentifier,
  SavedView,
  SavedViewCreate,
  SavedViewUpdate,
} from 'models/saved_view'
import { SuggestionCreate } from 'models/suggestion'
import { ChainsSummary, PlacesSummary } from 'models/summaries'
import { UniversityChainProxy } from 'models/universities'
import { Invitation, User } from 'models/user'
import {
  AutomationCreatePayload,
  AutomationMultipartPayload,
  AutomationListResponse,
  AutomationDetailResponse,
  AutomationCreateResponse,
} from 'models/automation'
import { useMemo } from 'react'
import {
  getCampaigns,
  useCreateCampaign,
  useCreateCampaignV2,
  useDeleteCampaign,
  useEditCampaign,
  useGetCampaignCompanies,
  useGetCampaigns,
  useGetCampaignsOptions,
} from '../features/campaigns/campaigns_api'
import {
  createRebateOffer,
  useGetRebateOffer,
  useGetRebateClaims,
  useGetRebateEnrollments,
  useGetRebateEnrollmentDetails,
  useGetOfferUserDetails,
  useManufacturerInvoices,
  getRebateDashboardStats,
  useGetRebateDashboardStats,
  getRebateOffers,
  useGetRebateOfferOptions,
  useGetCurrentManufacturerInvoice,
  getAttributionCodes,
  useGetAttributionCodes,
  createAttributionCode,
  updateAttributionCode,
  deleteAttributionCode,
  getRebateEnrollments,
} from '../features/tradespend/tradespend_api'
import { SalesPipeline } from '../models/SalesPipeline'
import { ChainListResponse } from '../models/chains'
import {
  IContactCompany,
  IContactCompanyCreateRequest,
  IContactCompanyUpdateRequest,
  IDoorProxy,
  IDoorProxyCreateRequest,
  IDoorProxyEditRequest,
  INote,
} from '../models/contact_companies'
import { Deal, DealEdit, DealHistory } from '../models/deals'
import {
  InfiniteQueryResponse,
  PaginatedResponse,
  StandardPaginatedResponse,
  StandardSummaryResponse,
} from '../models/paginated-response'
import { Product, Variant } from '../models/product'
import { SalesStage } from '../models/sales_stages'
import { Tag } from '../models/tags'
import getStageColor from '../utils/getStageColor'
import {
  DEFAULT_STAGE_COLOR,
  getPipelineColorForStage,
} from 'components/SalesStage/stageColors'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { clientInstance } from '../utils/http-client'
import { bulkEditSingleParams } from './apiTypes'
import {
  createColumnPreset,
  deleteColumnPreset,
  useGetAllColumnPresets,
  useGetColumnPresetOptions,
} from './column_presets_api'
import {
  createContactRequestJob,
  fetchContactRequestJobDetails,
  fetchContactRequestJobs,
  fetchRequestedContacts,
  useRequestContactsFromCampaign,
} from './contactRequestApi'
import {
  getK12DistrictsList,
  useGetK12District,
  useK12CountiesOptions,
  getK12DistrictsSummary,
} from './k12api'
import { queryClient } from './queryClient'
import { notifyError } from './toast'
import { ContactCampaign } from 'components/ImportContactFlow/ImportContactModal/ImportContactContext'
import {
  deleteEmailIntegration,
  getDistinctRecipients,
  getDistinctSenders,
  getEmailIntegrationAuthUrl,
  getEmailIntegrationList,
  getEmailMessageById,
  getEmailMessageList,
  getEmailThreadList,
  getThreadMessages,
  reauthenticateEmailIntegration,
  assignThreadsToDeal,
  removeThreadFromDeal,
  hideThread,
  unhideThread,
  searchDeals,
  sendEmailMessage,
  syncEmailIntegration,
  updateEmailIntegration,
  useGetEmailIntegrationOptions,
} from './emailIntegrationApi'
import { deleteGrant, getGrantsList, updateGrantPreferences } from './grantsApi'
import { getCalendars, selectCalendar } from './calendarsApi'
import {
  commonCreateDeals,
  commonExportTable,
  commonManageDistributors,
  commonManageTags,
  commonManageDealTags,
  commonManageGPOs,
  useAssignToCampaignMutation,
  useCommonGetCityStates,
  useCommonGetCityStatesInfinite,
  useUnassignToCampaignMutation,
} from './common_table_actions'
import { baseAPICall } from './baseAPICall'
import {
  ConvenienceChainProxy,
  LodgingChainProxy,
} from 'models/google_place_chain'
import {
  ConvenienceDoorProxy,
  LodgingDoorProxy,
} from 'models/google_place_door'

import { MISC_SETTINGS_TYPES } from 'models/misc_settings'
import { DoorProxyForDealsSelection as DoorProxyForSelection } from 'models/door_proxies_for_deals_selection'
import { UserNotificationSettingSummary } from 'models/notifications_preferences'
import {
  ReminderCreatePayload,
  ReminderDetailItem,
  ReminderListItem,
  ReminderUpdatePayload,
} from 'models/reminders'
import { ChainProxyReport } from 'models/chain_reports'
import { useAuth } from 'context/authentication/useAuth'
import { usePreferences } from 'context/preferences/PreferencesContext'

export type tagParent = 'products' | 'contacts' | 'documents' | 'deals'

interface CreditBalance {
  total: number
  available: number
  used: number
  pending: number
  on_hold?: number // For backward compatibility
}

interface CreditBalanceResponse {
  credits: CreditBalance
}

type UpdateReportingUnitRequest = {
  reporting_unit: string
}

export default function useApiService() {
  const { isLimited } = useAuth()
  const { isSettingEnabled } = usePreferences()
  const { dealDetailPage } = useFeatureFlagService()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  async function getProducts(params?: Record<string, any>) {
    return baseAPICall<PaginatedResponse<Product>>(() =>
      clientInstance.get('products/', { params })
    )
  }

  const useGetProducts = (disabled = false) =>
    useQuery({
      queryKey: ['products'],
      queryFn: async () => {
        const response = await getProducts()
        return response.results
      },
      refetchOnMount: false,
      enabled: !disabled,
    })

  async function getProductsOptions(withNoneOption?: boolean) {
    return baseAPICall<
      {
        label: string
        value: number
        original: Product
      }[]
    >(() =>
      clientInstance.get('products/', {
        params: { page_size: 1000 },
        transformResponse: (data) => {
          const response = JSON.parse(data)
          const products = response.results || []
          const opts = products.map((product: Product) => ({
            label: product.name,
            value: product.id,
            original: product,
          }))

          if (withNoneOption) {
            opts.unshift({ label: 'No Product', value: -1 })
          }

          return opts
        },
      })
    )
  }

  const useGetProductsOptions = (withNoneOption?: boolean) =>
    useQuery({
      queryKey: ['productsOptions', withNoneOption ? 'with-none' : ''],
      queryFn: () => getProductsOptions(withNoneOption),
    })

  async function getProductVariantsOptions(withNoneOption?: boolean) {
    return baseAPICall<
      {
        label: string
        value: number
        original: Variant
      }[]
    >(() =>
      clientInstance.get('products/', {
        params: { page_size: 1000 },
        transformResponse: (data) => {
          const response = JSON.parse(data)
          const products = response.results || []
          const variantOptions: {
            label: string
            value: number
            original: Variant
          }[] = []

          products.forEach((product: Product) => {
            if (product.variants && product.variants.length > 0) {
              product.variants.forEach((variant) => {
                if (variant.id) {
                  variantOptions.push({
                    label: `${product.name} - ${variant.name}`,
                    value: variant.id,
                    original: variant,
                  })
                }
              })
            }
          })

          if (withNoneOption) {
            variantOptions.unshift({
              label: 'No Variant',
              value: -1,
              original: {} as Variant,
            })
          }

          return variantOptions
        },
      })
    )
  }

  const useGetProductVariantsOptions = (withNoneOption?: boolean) =>
    useQuery({
      queryKey: ['productVariantsOptions', withNoneOption ? 'with-none' : ''],
      queryFn: () => getProductVariantsOptions(withNoneOption),
    })

  async function postProduct(values: Omit<Product, 'id'>) {
    delete values.company

    return baseAPICall<Product>(
      () => clientInstance.post('products/', values),
      { successMessage: 'Product created successfully' }
    )
  }

  async function putProduct(id: number, values: Omit<Product, 'id'>) {
    delete values.company

    return baseAPICall<Product>(
      () => clientInstance.put(`products/${id}/`, values),
      { successMessage: 'Product updated successfully' }
    )
  }

  async function deleteProduct(id: number) {
    return baseAPICall(() => clientInstance.delete(`products/${id}/`), {
      successMessage: 'Product deleted successfully',
    })
  }

  async function getRestaurantsV2(params = {}, signal?: AbortSignal) {
    return baseAPICall<PaginatedResponse<DoorProxyList>>(() =>
      clientInstance.get('restaurants/places/', {
        signal,
        params,
      })
    )
  }

  async function getPlacesSummaryV2(params = {}, signal?: AbortSignal) {
    return baseAPICall<PlacesSummary>(() =>
      clientInstance.get('restaurants/places/summary/', {
        signal,
        params,
        transformResponse: (data) => {
          return JSON.parse(data)
        },
      })
    )
  }

  const useGetPlacesSummaryV2 = (
    params: Record<string, any> = {},
    signal?: AbortSignal,
    enabled: boolean = true
  ) =>
    useQuery({
      queryKey: [DOOR_SUMMARY_KEY, params],
      queryFn: () => {
        return getPlacesSummaryV2({ ...params }, signal)
      },
      refetchOnMount: false,
      enabled: enabled,
    })

  async function getChainsSummaryV2(params = {}, signal?: AbortSignal) {
    return baseAPICall<ChainsSummary>(() =>
      clientInstance.get('restaurants/chains/summary/', {
        signal,
        params,
        transformResponse: (data) => {
          return JSON.parse(data)
        },
      })
    )
  }

  const useGetChainsSummaryV2 = (
    params = {},
    signal?: AbortSignal,
    enabled = true
  ) =>
    useQuery({
      queryKey: [CHAIN_SUMMARY_KEY, params],
      queryFn: () => {
        return getChainsSummaryV2({ ...params }, signal)
      },
      enabled,
    })

  async function getPlaceV2(placeId?: string): Promise<DoorProxyDetail> {
    if (!placeId) {
      notifyError('no place provided')
      throw new Error('no place provided')
    }
    const url = `restaurants/places/${placeId}/`
    const [err, res] = await to(clientInstance.get(url))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  const useGetPlaceV2 = (placeId?: string) =>
    useQuery<DoorProxyDetail, AxiosError>({
      queryKey: ['place', placeId],
      queryFn: () => getPlaceV2(placeId),

      enabled: !!placeId,
      retry: (failureCount, error: Error | AxiosError) => {
        return axios.isAxiosError(error) && error?.response?.status === 404
          ? false
          : failureCount < 3
      },
    })

  type ChainDetail = {
    distributors: any[]
    gpos: any[]
    taglist: any[]

    instagram: {
      followers: number
      following: number
      posts: number
      percentile: number
      text_percentile: string
      url: string
    }

    total_ltv_sum: number
    revenue_ltv_sum: number
    brand_ltv_sum: number

    domain: string

    logo: any
    pop_density: number
    median_hhi: number
    reviews_per_score: ReviewsPerScore
    rating: number
    reviews_count: number
    cuisine_50: string
    expense_category: string
    count: number
    website: string
    description: string
    chain: string
    id: number
    contact_company_id?: number
    // Location data
    hq_city1?: string
    hq_state1?: string
    hq_city2?: string
    hq_state2?: string
    city_summary?: string
    zip_summary?: string
    state_summary?: string
    dma_summary?: string
    // Traffic data
    velocity_group?: string
    // Menu data
    menu_length?: number
    // Characteristics data
    services?: string[]
    atmospheres?: string[]
    offerings?: string[]
    open_hours?: string[]
  }

  async function getChain(chainId?: number): Promise<ChainDetail> {
    if (!chainId) {
      notifyError('no chain provided')
      throw new Error('no chain provided')
    }

    const url = `restaurants/chains/${chainId}/`
    const [err, res] = await to(clientInstance.get(url))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  async function getUniversity(
    universityId?: number
  ): Promise<UniversityChainProxy> {
    if (!universityId) {
      notifyError('no university provided')
      throw new Error('no university provided')
    }

    const url = `universities/${universityId}/`
    const [err, res] = await to(clientInstance.get(url))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  async function getUniversitySummary(params = {}) {
    return baseAPICall<StandardSummaryResponse>(() =>
      clientInstance.get(`/universities/summary/`, {
        params,
      })
    )
  }

  async function getHospital(hospitalId?: number): Promise<HospitalChainProxy> {
    if (!hospitalId) {
      notifyError('no hospital provided')
      throw new Error('no hospital provided')
    }
    const url = `hospital/${hospitalId}/`
    const [err, res] = await to(clientInstance.get(url))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  async function getUniversitiesList(params = {}) {
    return baseAPICall<PaginatedResponse<UniversityChainProxy>>(() =>
      clientInstance.get(`/universities/`, {
        params,
      })
    )
  }

  async function getHospitalsList(params = {}) {
    return baseAPICall<PaginatedResponse<HospitalChainProxy>>(() =>
      clientInstance.get(`/hospital/`, {
        params,
      })
    )
  }

  async function getHospitalsSummary(params = {}) {
    return baseAPICall<StandardSummaryResponse>(() =>
      clientInstance.get(`/hospital/summary/`, {
        params,
      })
    )
  }

  async function getNursingHome(
    nursingHomeId?: number
  ): Promise<NursingHomeChainProxy> {
    if (!nursingHomeId) {
      notifyError('no nursing home provided')
      throw new Error('no nursing home provided')
    }
    const url = `nursing_home/${nursingHomeId}/`
    const [err, res] = await to(clientInstance.get(url))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  async function getNursingHomesList(params = {}) {
    return baseAPICall<PaginatedResponse<NursingHomeChainProxy>>(() =>
      clientInstance.get(`/nursing_home/`, {
        params,
      })
    )
  }

  async function getNursingHomesSummary(params = {}) {
    return baseAPICall<StandardSummaryResponse>(() =>
      clientInstance.get(`/nursing_home/summary/`, {
        params,
      })
    )
  }

  async function getLodgingList(params = {}) {
    return baseAPICall<PaginatedResponse<LodgingChainProxy>>(() =>
      clientInstance.get(`/places/lodging/`, {
        params,
      })
    )
  }

  async function getLodgingSummary(params = {}) {
    return baseAPICall<StandardSummaryResponse>(() =>
      clientInstance.get(`/places/lodging/summary/`, {
        params,
      })
    )
  }

  async function getLodgingDoorList(params = {}) {
    return baseAPICall<PaginatedResponse<LodgingDoorProxy>>(() =>
      clientInstance.get(`/places/lodging-doors/`, {
        params,
      })
    )
  }

  async function getLodgingDoorSummary(params = {}) {
    return baseAPICall<StandardSummaryResponse>(() =>
      clientInstance.get(`/places/lodging-doors/summary/`, {
        params,
      })
    )
  }

  const useGetChain = (chainId?: number) =>
    useQuery<ChainDetail, AxiosError>({
      queryKey: ['chain', chainId],
      queryFn: () => getChain(chainId),
      enabled: !!chainId, // Only fetch when chainId is truthy
      retry: (failureCount, error: Error | AxiosError) => {
        // By defualt, useQuery performs 3 retries on network errors
        // No retry on 404 error; continue on other errors up to 3 times
        return axios.isAxiosError(error) && error?.response?.status === 404
          ? false
          : failureCount < 3
      },
    })

  async function getUsers() {
    return baseAPICall<User[]>(() => clientInstance.get('users/'))
  }

  async function getUserTypes() {
    return baseAPICall<{ label: string; value: string }[]>(() =>
      clientInstance.get('users/user-types/')
    )
  }

  async function updateUserType(
    userId: number,
    userType: string,
    rsmAssignmentId?: number | null
  ) {
    return baseAPICall(() =>
      clientInstance.put(`users/${userId}/user-type/`, {
        user_type: userType,
        rsm_assignment_id: rsmAssignmentId ?? null,
      })
    )
  }

  async function getRSMEligibleUsers() {
    const params = new URLSearchParams()
    params.append('user_type', 'Standard')
    params.append('user_type', 'Admin')
    params.append('is_active', 'true')
    return baseAPICall<User[]>(() =>
      clientInstance.get(`users/?${params.toString()}`)
    )
  }

  const useGetUniversity = (universityId?: number) =>
    useQuery({
      queryKey: ['university', universityId],
      queryFn: () => getUniversity(universityId),
      enabled: !!universityId,
      retry: (failureCount, error: Error | AxiosError) => {
        // By defualt, useQuery performs 3 retries on network errors
        // No retry on 404 error; continue on other errors up to 3 times
        return axios.isAxiosError(error) && error?.response?.status === 404
          ? false
          : failureCount < 3
      },
    })

  const useGetHospital = (hospitalId?: number) =>
    useQuery({
      queryKey: ['hospital', hospitalId],
      queryFn: () => getHospital(hospitalId),
      enabled: !!hospitalId,
      retry: (failureCount, error: Error | AxiosError) => {
        // By default, useQuery performs 3 retries on network errors
        // No retry on 404 error; continue on other errors up to 3 times
        return axios.isAxiosError(error) && error?.response?.status === 404
          ? false
          : failureCount < 3
      },
    })

  async function getConvenienceList(params = {}) {
    return baseAPICall<PaginatedResponse<ConvenienceChainProxy>>(() =>
      clientInstance.get(`/places/convenience/`, {
        params,
      })
    )
  }

  async function getConvenienceSummary(params = {}) {
    return baseAPICall<StandardSummaryResponse>(() =>
      clientInstance.get(`/places/convenience/summary/`, {
        params,
      })
    )
  }

  async function getConvenienceDoorList(params = {}) {
    return baseAPICall<PaginatedResponse<ConvenienceDoorProxy>>(() =>
      clientInstance.get(`/places/convenience-doors/`, {
        params,
      })
    )
  }

  async function getConvenienceDoorSummary(params = {}) {
    return baseAPICall<StandardSummaryResponse>(() =>
      clientInstance.get(`/places/convenience-doors/summary/`, {
        params,
      })
    )
  }

  async function getLodging(lodging?: number): Promise<LodgingChainProxy> {
    const url = `places/lodging/${lodging}/`
    const [err, res] = await to(clientInstance.get(url))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  const useGetLodging = (lodgingId?: number) =>
    useQuery({
      queryKey: ['lodging', lodgingId],
      queryFn: () => getLodging(lodgingId),
      enabled: !!lodgingId,
      retry: (failureCount, error: Error | AxiosError) => {
        // By default, useQuery performs 3 retries on network errors
        // No retry on 404 error; continue on other errors up to 3 times
        return axios.isAxiosError(error) && error?.response?.status === 404
          ? false
          : failureCount < 3
      },
    })

  async function getLodgingDoor(lodging?: number): Promise<LodgingDoorProxy> {
    const url = `places/lodging-doors/${lodging}/`
    const [err, res] = await to(clientInstance.get(url))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  const useGetLodgingDoor = (lodgingDoorId?: number) =>
    useQuery({
      queryKey: ['lodging', lodgingDoorId],
      queryFn: () => getLodgingDoor(lodgingDoorId),
      enabled: !!lodgingDoorId,
      retry: (failureCount, error: Error | AxiosError) => {
        // By default, useQuery performs 3 retries on network errors
        // No retry on 404 error; continue on other errors up to 3 times
        return axios.isAxiosError(error) && error?.response?.status === 404
          ? false
          : failureCount < 3
      },
    })

  async function getConvenience(
    convenience?: number
  ): Promise<ConvenienceChainProxy> {
    const url = `places/convenience/${convenience}/`
    const [err, res] = await to(clientInstance.get(url))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  const useGetConvenience = (convenienceId?: number) =>
    useQuery({
      queryKey: ['convenience', convenienceId],
      queryFn: () => getConvenience(convenienceId),
      enabled: !!convenienceId,
      retry: (failureCount, error: Error | AxiosError) => {
        // By default, useQuery performs 3 retries on network errors
        // No retry on 404 error; continue on other errors up to 3 times
        return axios.isAxiosError(error) && error?.response?.status === 404
          ? false
          : failureCount < 3
      },
    })

  async function getConvenienceDoor(
    convenience?: number
  ): Promise<ConvenienceDoorProxy> {
    const url = `places/convenience-doors/${convenience}/`
    const [err, res] = await to(clientInstance.get(url))

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res.data
  }

  const useGetConvenienceDoor = (convenienceDoorId?: number) =>
    useQuery({
      queryKey: ['convenience', convenienceDoorId],
      queryFn: () => getConvenienceDoor(convenienceDoorId),
      enabled: !!convenienceDoorId,
      retry: (failureCount, error: Error | AxiosError) => {
        // By default, useQuery performs 3 retries on network errors
        // No retry on 404 error; continue on other errors up to 3 times
        return axios.isAxiosError(error) && error?.response?.status === 404
          ? false
          : failureCount < 3
      },
    })

  const useGetNursingHome = (nursingHomeId?: number) =>
    useQuery({
      queryKey: ['nursing_home', nursingHomeId],
      queryFn: () => getNursingHome(nursingHomeId),
      enabled: !!nursingHomeId,
      retry: (failureCount, error: Error | AxiosError) => {
        return axios.isAxiosError(error) && error?.response?.status === 404
          ? false
          : failureCount < 3
      },
    })

  const useGetUsers = (disabled = isLimited) =>
    useQuery({
      queryKey: ['users'],
      queryFn: getUsers,
      enabled: !disabled,
      refetchOnMount: false,
    })

  const useGetUserOptions = ({
    withNoneOption,
    onlyActive,
  }: {
    withNoneOption?: boolean
    onlyActive?: boolean
  }) => {
    const { data: users } = useGetUsers()
    const opts = useMemo(() => {
      if (!users) return
      const out:
        | {
            label: string
            value: number
            original?: User
          }[]
        | undefined = users
        .filter((user: User) => {
          if (onlyActive) {
            return user.is_active
          }
          return true
        })
        .map((user: User) => ({
          label: user.first_name + ' ' + user.last_name,
          value: user.id,
          original: user,
        }))

      if (withNoneOption) {
        out.unshift({ label: 'No User', value: -1 })
      }

      return out
    }, [users, withNoneOption])

    return {
      data: opts,
      isLoading: !users,
      isError: false,
    }
  }

  async function bulkEditCompanyAttrs(values: {
    chain_ids?: number[]
    contact_company_ids?: number[]
    taglist?: 'REMOVE' | number[]
    distributors?: 'REMOVE' | number[]
    gpos?: 'REMOVE' | number[]
    campaigns?: 'REMOVE' | number[]
    domain?: string
    previous_taglist?: number[]
    previous_distributors?: number[]
    previous_gpos?: number[]
    previous_campaigns?: number[]
  }) {
    return baseAPICall(() =>
      clientInstance.post('contacts/companyattrs-bulk/', values)
    )
  }

  async function bulkEditDoorAttrs(values: {
    door_proxy_ids: number[]
    taglist?: 'REMOVE' | number[]
    distributors?: 'REMOVE' | number[]
    gpos?: 'REMOVE' | number[]
    previous_taglist?: number[]
    previous_distributors?: number[]
    previous_gpos?: number[]
  }) {
    return baseAPICall(() =>
      clientInstance.post('contacts/doorattrs-bulk/', values)
    )
  }

  async function bulkEditDeal(
    values: {
      deal: {
        account_owner_id?: number | null
        sales_stage_id?: number | null
        name?: string | null
        monthly_revenue_override?: number | null
        monthly_volume_override?: number | null
        close_date_override?: string | null
      }
    } & Omit<bulkEditSingleParams, 'item_id'>
  ) {
    const { params, ...rest } = values
    const endpoint = 'contacts/deals/bulk/'

    const [err, res] = await to(
      clientInstance.post(endpoint, rest, {
        params,
      })
    )

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res
  }

  async function bulkEditDealReports(
    values: {
      deal: {
        account_owner_id?: number | null
        sales_stage_id?: number | null
        name?: string | null
        monthly_revenue_override?: number | null
        monthly_volume_override?: number | null
        close_date_override?: string | null
      }
    } & Omit<bulkEditSingleParams, 'item_id'>
  ) {
    const { params, ...rest } = values
    const endpoint = 'reports/deals/bulk/update/'

    const [err, res] = await to(
      clientInstance.post(endpoint, rest, {
        params,
      })
    )

    if (err) {
      notifyError(err.message)
      throw err
    }

    return res
  }

  async function getSalesPipelineV2(params = {}, signal?: AbortSignal) {
    return baseAPICall<SalesPipeline>(() =>
      clientInstance.get('reports/dashboard/sales-pipeline/', {
        signal,
        params,
      })
    )
  }

  async function getSalesPipeline(params = {}, signal?: AbortSignal) {
    return baseAPICall<SalesPipeline>(() =>
      clientInstance.get('products/sales_pipeline_v2/', {
        signal,
        params,
      })
    )
  }

  async function getSalesStages(signal?: AbortSignal) {
    return baseAPICall<SalesStage[]>(() =>
      clientInstance.get('products/sales_stages/', {
        signal,
      })
    )
  }

  const useGetSalesStages = (disabled = isLimited) =>
    useQuery({
      queryKey: ['stages'],
      queryFn: () => getSalesStages(),
      refetchOnMount: false,
      enabled: !disabled,
    })

  const useGetSalesStageOptions = (withNoneOption?: boolean) => {
    const { data } = useGetSalesStages()
    const out = useMemo(() => {
      if (!data) return
      const stages = customOrderEnabled
        ? [...data].sort((a, b) => a.order - b.order)
        : data
      const out:
        | {
            label: string
            value: number
            color: string
            original?: SalesStage
          }[]
        | undefined = stages.map((s: SalesStage) => ({
        label: s.stage,
        value: s.id,
        color: dealDetailPage
          ? (getPipelineColorForStage(s.id, data, customOrderEnabled)?.bg ??
            DEFAULT_STAGE_COLOR)
          : getStageColor(s.win_rate)[0],
        original: s,
      }))

      if (withNoneOption) {
        out?.unshift({
          label: 'No Stage',
          value: -1,
          color: '#fff',
        })
      }

      return out
    }, [data, withNoneOption, customOrderEnabled, dealDetailPage])

    return {
      data: out,
      isLoading: !data,
      isError: false,
    }
  }

  async function getTags(tagParent: tagParent = 'products') {
    // Documents and deals endpoints use ?simple=true for non-paginated autocomplete response
    const params =
      tagParent === 'documents' || tagParent === 'deals'
        ? { simple: 'true' }
        : {}
    // Deal tags are under contacts/deals/tags/
    const urlPrefix = tagParent === 'deals' ? 'contacts/deals' : tagParent
    return baseAPICall<Tag[]>(() =>
      clientInstance.get(`${urlPrefix}/tags/`, { params })
    )
  }

  async function getProductTagsList(
    params: Record<string, string | number> = {},
    signal?: AbortSignal
  ) {
    return baseAPICall<PaginatedResponse<Tag>>(() =>
      clientInstance.get(`products/tags-list/`, {
        params: params,
        signal,
      })
    )
  }

  async function getContactTagsList(
    params: Record<string, string | number> = {},
    signal?: AbortSignal
  ) {
    return baseAPICall<PaginatedResponse<Tag>>(() =>
      clientInstance.get(`contacts/tags-list/`, {
        params: params,
        signal,
      })
    )
  }

  async function getDocumentTagsList(
    params: Record<string, string | number> = {},
    signal?: AbortSignal
  ) {
    return baseAPICall<PaginatedResponse<Tag>>(() =>
      clientInstance.get(`documents/tags/`, {
        params: params,
        signal,
      })
    )
  }

  async function getDealTagsList(
    params: Record<string, string | number> = {},
    signal?: AbortSignal
  ) {
    return baseAPICall<PaginatedResponse<Tag>>(() =>
      clientInstance.get(`contacts/deals/tags/`, {
        params: params,
        signal,
      })
    )
  }

  const useGetTags = (
    tagParent: tagParent = 'products',
    disabled = isLimited
  ) =>
    useQuery({
      queryKey: ['tags', tagParent],
      queryFn: () => getTags(tagParent),
      enabled: !disabled,
      refetchOnMount: false,
    })

  const useGetTagsOptions = (
    withNoneOption?: boolean,
    tagParent: tagParent = 'products'
  ) => {
    const { data: tags } = useGetTags(tagParent)
    const opts = useMemo(() => {
      const opts:
        | {
            label: string
            value: number | string
            original?: Tag
          }[]
        | undefined = tags?.map((tag: any) => ({
        label: tag.tag,
        value: tag.id,
        original: tag,
      }))

      if (withNoneOption) {
        opts?.unshift({ label: 'No Tag', value: 'REMOVE' })
      }
      return opts
    }, [tags, withNoneOption])

    return {
      data: opts,
      isLoading: !tags,
      isError: false,
    }
  }

  async function postSalesStage(data: any) {
    return baseAPICall(() =>
      clientInstance.post('products/sales_stages/', data)
    )
  }

  async function postTag(data: any, tagParent: tagParent = 'products') {
    const urlPrefix = tagParent === 'deals' ? 'contacts/deals' : tagParent
    return baseAPICall(() =>
      clientInstance.post(`${urlPrefix}/tags/`, data)
    ).then((res) => {
      queryClient.invalidateQueries({ queryKey: ['tags', tagParent] })
      return res
    })
  }

  async function putTag(
    id: number,
    data: { tag: string; description?: string | null; color: string },
    tagParent: tagParent = 'products'
  ) {
    const urlPrefix = tagParent === 'deals' ? 'contacts/deals' : tagParent
    return baseAPICall(() =>
      clientInstance.put(`${urlPrefix}/tags/${id}/`, data)
    )
  }

  async function putStage(id: number, data: any) {
    return baseAPICall(() =>
      clientInstance.put(`products/sales_stages/${id}/`, data)
    )
  }

  async function deleteStage(id: number) {
    return baseAPICall(() =>
      clientInstance.delete(`products/sales_stages/${id}/`)
    )
  }

  async function bulkReorderSalesStages(orderedStageIds: number[]) {
    return baseAPICall(() =>
      clientInstance.post('products/sales_stages/bulk-reorder/', {
        ordered_stage_ids: orderedStageIds,
      })
    )
  }

  async function deleteTag(id: number, tagParent: tagParent = 'products') {
    const urlPrefix = tagParent === 'deals' ? 'contacts/deals' : tagParent
    return baseAPICall(() => clientInstance.delete(`${urlPrefix}/tags/${id}/`))
  }

  async function getDealsPipelineV2(params = {}, signal?: AbortSignal) {
    return baseAPICall<DealsPipeline[]>(() =>
      clientInstance.get('reports/dashboard/deals-pipeline/', {
        signal,
        params,
      })
    )
  }

  async function getDealsPipeline(params = {}, signal?: AbortSignal) {
    return baseAPICall<DealsPipeline[]>(() =>
      clientInstance.get('products/deals-pipeline/', {
        signal,
        params,
      })
    )
  }

  async function getChainsListV2(params = {}, signal?: AbortSignal) {
    return baseAPICall<ChainListResponse>(() =>
      clientInstance.get('restaurants/chains/list/', {
        signal,
        params,
      })
    )
  }

  async function getChainMenuList(
    chainId?: number,
    params = {},
    signal?: AbortSignal
  ) {
    if (!chainId) {
      return [] as Menu[]
    }
    return baseAPICall<Menu[]>(() =>
      clientInstance.get(`restaurants/chains/${chainId}/menu/`, {
        signal,
        params,
      })
    )
  }

  const useGetChainMenuList = (chainId?: number, params = {}) =>
    useQuery({
      queryKey: ['chain_menu_list', chainId, params],
      queryFn: () => getChainMenuList(chainId, params),
      refetchOnMount: false,
      enabled: !!chainId,
    })

  async function getDoorsMenuList(
    doorId?: number,
    params = {},
    signal?: AbortSignal
  ) {
    if (!doorId) {
      return [] as Menu[]
    }
    return baseAPICall<Menu[]>(() =>
      clientInstance.get(`restaurants/places/${doorId}/menu/`, {
        signal,
        params,
      })
    )
  }

  const useGetDoorsMenuList = (doorId?: number, params = {}) =>
    useQuery({
      queryKey: ['doors_menu_list', doorId, params],
      queryFn: () => getDoorsMenuList(doorId, params),
      refetchOnMount: false,
      enabled: !!doorId,
    })

  async function getContactsList(
    params = {},
    signal?: AbortSignal
  ): Promise<ContactListResponse> {
    return baseAPICall<ContactListResponse>(() =>
      clientInstance.get('contacts/', {
        signal,
        params,
      })
    )
  }

  async function getContactsCounts(params = {}, signal?: AbortSignal) {
    return baseAPICall<{ all_contacts_count: number; requests_count: number }>(
      () =>
        clientInstance.get(`contacts/mobile-counts/`, {
          params,
          signal,
        })
    )
  }

  function useGetContactsList(
    params = {},
    signal?: AbortSignal,
    enabled = true
  ) {
    return useQuery({
      queryKey: ['contacts', params],
      queryFn: () => getContactsList(params, signal),
      refetchOnMount: false,
      enabled,
    })
  }

  const useGetContactsInfinite = (params: {
    page_size: number
    page: number
  }) =>
    useInfiniteQuery({
      initialPageParam: 1,
      queryKey: ['contacts', params],
      queryFn: ({ pageParam = 1 }) =>
        getContactsList({ ...params, page: pageParam }),
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.results.length < params.page_size) return undefined
        return allPages.length + 1
      },
    })

  const useGetLocationsInfinite = (
    chainProxyId: number,
    params: Record<string, any>
  ) =>
    useInfiniteQuery({
      initialPageParam: 1,
      queryKey: ['doors-locations', chainProxyId, params],
      queryFn: ({ pageParam = 1 }) =>
        getDoorsForDealSelection(
          chainProxyId,
          { ...params, page: pageParam },
          {}
        ),
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.results.length < params.page_size) return undefined
        return allPages.length + 1
      },
    })

  const useGetContactRequestsInfinite = (params: {
    limit: number
    page: number
  }) =>
    useInfiniteQuery({
      initialPageParam: 1,
      queryKey: ['contact_requests', params],
      queryFn: ({ pageParam = 1 }) =>
        fetchContactRequestJobs({ ...params, page: pageParam }),
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.results.length < params.limit) return undefined
        return allPages.length + 1
      },
    })

  const useGetContactsCounts = (params = {}) =>
    useQuery({
      queryKey: ['contacts-counts', params],
      queryFn: () => getContactsCounts(params),
    })

  async function getActivityList(
    params = {},
    signal?: AbortSignal
  ): Promise<ActivityListResponse> {
    return baseAPICall<ActivityListResponse>(() =>
      clientInstance.get('activity/team/', {
        signal,
        params,
      })
    )
  }

  async function getMyActivityList(
    params = {},
    signal?: AbortSignal
  ): Promise<ActivityListResponse> {
    return baseAPICall<ActivityListResponse>(() =>
      clientInstance.get('activity/my/', {
        signal,
        params,
      })
    )
  }

  async function getActivityListV2(
    params = {},
    signal?: AbortSignal
  ): Promise<ActivityListResponseV2> {
    return baseAPICall<ActivityListResponseV2>(() =>
      clientInstance.get('activity/v2/team/', {
        signal,
        params,
      })
    )
  }

  async function getMyActivityListV2(
    params = {},
    signal?: AbortSignal
  ): Promise<ActivityListResponseV2> {
    return baseAPICall<ActivityListResponseV2>(() =>
      clientInstance.get('activity/v2/my/', {
        signal,
        params,
      })
    )
  }

  async function getCompanyActivityList(
    contactCompanyId: number,
    params = {},
    signal?: AbortSignal
  ): Promise<ActivityListResponseV2> {
    return baseAPICall<ActivityListResponseV2>(() =>
      clientInstance.get('activity/v2/team/', {
        signal,
        params: {
          contact_company_id: contactCompanyId,
          include_emails: true,
          ...params,
        },
      })
    )
  }

  /** Fetch activity list filtered to a specific deal (includes emails). */
  async function getDealActivityList(
    dealId: number,
    params = {},
    signal?: AbortSignal
  ): Promise<ActivityListResponseV2> {
    return baseAPICall<ActivityListResponseV2>(() =>
      clientInstance.get('activity/v2/team/', {
        signal,
        params: {
          deal_id: dealId,
          include_emails: true,
          ...params,
        },
      })
    )
  }

  async function getActiveCompanyCategoriesAndTypes() {
    const res = await baseAPICall<Category[]>(() =>
      clientInstance.get('contacts/companies-by-category/')
    )

    res.sort((a, b) => a.name.charCodeAt(0) - b.name.charCodeAt(0))

    return res
  }

  const useGetActiveCompanyCategoriesAndTypes = (disabled = isLimited) =>
    useQuery({
      queryKey: ['companyCategoriesAndSub'],
      queryFn: () => getActiveCompanyCategoriesAndTypes(),
      enabled: !disabled,
      refetchOnMount: false,
    })

  async function createContact(data: ContactCreateRequest) {
    return baseAPICall(() => clientInstance.post('contacts/create/', data))
  }

  async function updateContact(id: number, data: ContactCreateRequest) {
    return baseAPICall(() =>
      clientInstance.patch(`contacts/update/${id}/`, data)
    )
  }

  async function deleteContact(id: number) {
    return baseAPICall(() => clientInstance.delete(`contacts/${id}/`))
  }

  async function getContactDetails(contactId: number, signal?: AbortSignal) {
    return baseAPICall<ContactResponseDetail>(() =>
      clientInstance.get(`contacts/${contactId}/`, {
        signal,
      })
    )
  }

  function useGetContactDetails(
    contactId: number,
    signal?: AbortSignal,
    enabled = true
  ) {
    return useQuery({
      queryKey: ['contact', contactId],
      queryFn: () => getContactDetails(contactId, signal),
      refetchOnMount: false,
      enabled,
    })
  }

  function getContactsImportFormData(
    file: File,
    column_mapping: any,
    company_mapping: any = null,
    tags: number[] = [],
    campaigns: ContactCampaign[] = [],
    subcategory_id?: number
  ): FormData {
    const formData = new FormData()
    formData.append('file', file)
    const jsonBlob = new Blob([JSON.stringify(column_mapping)], {
      type: 'application/json',
    })
    formData.append('column_mapping', jsonBlob)
    if (company_mapping) {
      const jsonBlob = new Blob([JSON.stringify(company_mapping)], {
        type: 'application/json',
      })
      formData.append('company_mapping', jsonBlob)
    }
    if (tags.length > 0) {
      const jsonBlob = new Blob([JSON.stringify(tags)], {
        type: 'application/json',
      })
      formData.append('tags', jsonBlob)
    }
    if (campaigns.length > 0) {
      const jsonBlob = new Blob([JSON.stringify(campaigns)], {
        type: 'application/json',
      })
      formData.append('campaigns', jsonBlob)
    }
    if (subcategory_id) {
      formData.append('subcategory_id', subcategory_id.toString())
    }
    return formData
  }

  async function previewContactsImport(
    file: File,
    column_mapping: any,
    company_mapping: any = null,
    subcategory_id?: number
  ): Promise<ContactsImportResponse> {
    const formData = getContactsImportFormData(
      file,
      column_mapping,
      company_mapping,
      [],
      [],
      subcategory_id
    )
    return baseAPICall(() =>
      clientInstance.post('contacts/import/preview/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    )
  }

  async function contactsImport(
    file: File,
    column_mapping: any,
    company_mapping: any = null,
    tags: number[] = [],
    campaigns: ContactCampaign[] = [],
    subcategory_id?: number
  ): Promise<ContactsImportResponse> {
    const formData = getContactsImportFormData(
      file,
      column_mapping,
      company_mapping,
      tags,
      campaigns,
      subcategory_id
    )
    return baseAPICall(() =>
      clientInstance.post('contacts/import/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    )
  }

  async function getCompanyCategories() {
    return baseAPICall<Category[]>(() =>
      clientInstance.get('contacts/categories/')
    )
  }

  const useGetCompanyCategories = (enabled = true) =>
    useQuery({
      queryKey: ['company_categories'],
      queryFn: getCompanyCategories,
      enabled: enabled,
      refetchOnMount: false,
    })

  async function getCompaniesList(subCategory: number, params = {}) {
    return baseAPICall<PaginatedResponse<IContactCompany>>(() =>
      clientInstance.get(`/contacts/contactcompanies/`, {
        params: {
          sub_category_id: subCategory,
          ...params,
        },
      })
    )
  }

  async function getCompaniesSummary(subCategory: number, params = {}) {
    return baseAPICall<{ count: number; alt_count: number }>(() =>
      clientInstance.get(`/contacts/contactcompanies/summary/`, {
        params: {
          sub_category_id: subCategory,
          ...params,
        },
      })
    )
  }

  async function getCompany(id: number): Promise<IContactCompany> {
    return baseAPICall(() =>
      clientInstance.get(`contacts/contactcompanies/${id}/`)
    )
  }

  const useGetCompany = (companyId: number | null | undefined) =>
    useQuery<IContactCompany, AxiosError>({
      queryKey: ['company', companyId],
      queryFn: () => getCompany(companyId!),
      enabled: !!companyId,
      retry: (failureCount, error: AxiosError) => {
        return axios.isAxiosError(error) && error?.response?.status === 404
          ? false
          : failureCount < 3
      },
    })

  async function resolveCompanyFromFbIds(params: {
    chainId?: number
    doorId?: number
    type: string
  }): Promise<{
    contact_company_id: number
    chain_id?: number
    door_id?: number
    door_proxy_id?: number
    type: string
    name: string
    address_line1: string | null
    city: string | null
    state: string | null
    zipcode: string | null
  }> {
    const queryParams: any = { type: params.type }
    if (params.chainId) {
      queryParams.chain_id = params.chainId
    }
    if (params.doorId) {
      queryParams.door_id = params.doorId
    }

    return baseAPICall(() =>
      clientInstance.get(`contacts/resolve-company-from-fb-ids/`, {
        params: queryParams,
      })
    )
  }

  async function createCompany(
    data: IContactCompanyCreateRequest
  ): Promise<IContactCompany> {
    return baseAPICall(
      () => clientInstance.post(`contacts/contactcompanies/`, data),
      { disableNotifyGenericError: true }
    )
  }

  async function createDoorProxy(
    data: IDoorProxyCreateRequest
  ): Promise<IContactCompany> {
    return baseAPICall(() => clientInstance.post(`contacts/doorproxies/`, data))
  }

  async function editDoorProxy(
    data: IDoorProxyEditRequest,
    doorProxyId: number
  ): Promise<IDoorProxy> {
    return baseAPICall<IDoorProxy>(() =>
      clientInstance.patch(`contacts/doorproxies/${doorProxyId}/`, data)
    )
  }

  async function updateCompany(
    id: number,
    data: IContactCompanyUpdateRequest
  ): Promise<IContactCompany> {
    return baseAPICall(() =>
      clientInstance.patch(`contacts/contactcompanies/${id}/`, data)
    )
  }

  async function getCompanyDistributorsOptions(withNoneOption?: boolean) {
    return baseAPICall<
      {
        label: string
        value: number
        original: CompanyDistributor
      }[]
    >(() =>
      clientInstance.get('contacts/companies/distributors/', {
        transformResponse: (data) => {
          const distributors = JSON.parse(data)
          const opts = distributors.map((distributor: CompanyDistributor) => ({
            label: distributor.name,
            value: distributor.id,
            original: distributor,
          }))

          if (withNoneOption) {
            opts.unshift({ label: 'No Distributor', value: -1 })
          }

          return opts
        },
      })
    )
  }

  async function getCompanyGPOsOptions(withNoneOption?: boolean) {
    return baseAPICall<
      {
        label: string
        value: number
        original: CompanyGPO
      }[]
    >(() =>
      clientInstance.get('contacts/companies/gpos/', {
        transformResponse: (data) => {
          const gpos = JSON.parse(data)
          const opts = gpos.map((gpo: CompanyGPO) => ({
            label: gpo.name,
            value: gpo.id,
            original: gpo,
          }))

          if (withNoneOption) {
            opts.unshift({ label: 'No GPO', value: -1 })
          }

          return opts
        },
      })
    )
  }

  async function getUniversityFoodManagementCompanyOptions(
    withNoneOption?: boolean
  ) {
    return baseAPICall<{
      food_management_companies: any[]

      parent_food_management_companies: any[]
    }>(() =>
      clientInstance.get('universities/food_management_companies', {
        transformResponse: (data) => {
          const companies = JSON.parse(data)
          const options = {
            food_management_companies: [],
            parent_food_management_companies: [],
          } as any

          companies.food_management_companies.forEach((company: string) => {
            options.food_management_companies.push({
              label: company,
              value: company,
            })
          })

          companies.parent_food_management_companies.forEach(
            (parentCompany: string) => {
              options.parent_food_management_companies.push({
                label: parentCompany,
                value: parentCompany,
              })
            }
          )

          if (withNoneOption) {
            options.food_management_companies.unshift({
              label: 'No Company',
              value: '',
            })
            options.parent_food_management_companies.unshift({
              label: 'No Company',
              value: '',
            })
          }

          return options
        },
      })
    )
  }

  const useGetDistributors = () =>
    useQuery({
      queryKey: ['base-distributors'],
      queryFn: () => getDistributors(),
      refetchOnMount: false,
    })

  const useGetCompanyDistributorsOptions = (withNoneOption?: boolean) =>
    useQuery({
      queryKey: ['distributors', withNoneOption ? 'with-none' : ''],
      queryFn: () => getCompanyDistributorsOptions(withNoneOption),
      refetchOnMount: false,
    })

  const useGetCompanyGPOsOptions = (withNoneOption?: boolean) =>
    useQuery({
      queryKey: ['gpos', withNoneOption ? 'with-none' : ''],
      queryFn: () => getCompanyGPOsOptions(withNoneOption),
      refetchOnMount: false,
    })

  const useGetUniversityFoodManagementCompanyOptions = (
    withNoneOption?: boolean
  ) =>
    useQuery({
      queryKey: [
        'food_management_companies',
        withNoneOption ? 'with-none' : '',
      ],
      queryFn: () => getUniversityFoodManagementCompanyOptions(withNoneOption),
      refetchOnMount: false,
    })

  const postDistributor = async (data: CompanyDistributorCreate) => {
    return baseAPICall(() =>
      clientInstance.post(`contacts/companies/distributors/`, data)
    )
  }

  const postGPO = async (data: CompanyGPOCreate) => {
    return baseAPICall(() =>
      clientInstance.post(`contacts/companies/gpos/`, data)
    )
  }

  const postCampaign = async (data: CampaignCreate) => {
    return baseAPICall(() =>
      clientInstance.post(`contacts/campaigns/create/`, {
        name: data.name,
        type_slug: data.type_slug || 'foodservice',
        color: data.color || '#3B82F6',
        description: data.description || '',
      })
    )
  }

  const updateDistributor = async (
    id: number,
    data: Partial<CompanyDistributorCreate>
  ) => {
    return baseAPICall(() =>
      clientInstance.patch(`contacts/companies/distributors/${id}/`, data)
    )
  }

  const updateGPO = async (id: number, data: Partial<CompanyGPOCreate>) => {
    return baseAPICall(() =>
      clientInstance.patch(`contacts/companies/gpos/${id}/`, data)
    )
  }

  const deleteDistributor = async (id: number) => {
    return baseAPICall(() =>
      clientInstance.delete(`contacts/companies/distributors/${id}/`)
    )
  }

  const deleteGPO = async (id: number) => {
    return baseAPICall(() =>
      clientInstance.delete(`contacts/companies/gpos/${id}/`)
    )
  }

  async function getDistributors({
    signal,
    params = {},
  }: { signal?: AbortSignal; params?: Record<string, unknown> } = {}) {
    return baseAPICall<CompanyDistributor[]>(() =>
      clientInstance.get('contacts/companies/distributors/', {
        params,
        signal,
      })
    )
  }

  async function getGPOs({
    signal,
    params = {},
  }: { signal?: AbortSignal; params?: Record<string, unknown> } = {}) {
    return baseAPICall<CompanyGPO[]>(() =>
      clientInstance.get('contacts/companies/gpos/', {
        params,
        signal,
      })
    )
  }

  const useGetGPOs = () =>
    useQuery({
      queryKey: ['base-gpos'],
      queryFn: () => getGPOs(),
      refetchOnMount: false,
    })

  const getProductPredictions = async ({
    chainProxyId,
    doorProxyId,
    hasVariants,
  }: {
    chainProxyId?: number
    doorProxyId?: number
    hasVariants?: boolean
  }): Promise<ProductPrediction[]> => {
    const params: Record<string, any> = {}
    if (chainProxyId) {
      params['chain_proxy_id'] = chainProxyId
    }
    if (doorProxyId) {
      params['door_proxy_id'] = doorProxyId
    }
    if (hasVariants !== undefined) {
      params['has_variants'] = hasVariants ? 'true' : 'false'
    }
    return baseAPICall<ProductPrediction[]>(() =>
      clientInstance.get(`products/predictions/`, {
        params,
      })
    )
  }

  const useGetProductPredictions = ({
    chainProxyId,
    doorProxyId,
    hasVariants,
    enabled = true,
    noCache = false,
  }: {
    chainProxyId?: number
    doorProxyId?: number
    hasVariants?: boolean
    enabled?: boolean
    noCache?: boolean
  }) =>
    useQuery({
      queryKey: ['product_predictions', chainProxyId, doorProxyId, hasVariants],
      queryFn: () =>
        getProductPredictions({ chainProxyId, doorProxyId, hasVariants }),
      enabled,
      ...(noCache && {
        refetchOnMount: 'always' as const,
        staleTime: 0,
        gcTime: 0,
      }),
    })

  async function getDeals(params = {}, signal?: AbortSignal) {
    return baseAPICall<InfiniteQueryResponse<Deal>>(() =>
      clientInstance.get(`contacts/deals/`, {
        params,
        signal,
      })
    )
  }

  async function getDealsCounts(params = {}, signal?: AbortSignal) {
    return baseAPICall<{ my_deals_count: number; all_deals_count: number }>(
      () =>
        clientInstance.get(`contacts/deals/mobile-counts/`, {
          params,
          signal,
        })
    )
  }

  async function getReportDeals(params = {}, signal?: AbortSignal) {
    return baseAPICall<PaginatedResponse<DealReport>>(() =>
      clientInstance.get(`reports/deals/`, {
        params,
        signal,
      })
    )
  }

  async function getDoorsWithCRMReports(params = {}, signal?: AbortSignal) {
    return baseAPICall<PaginatedResponse<any>>(() =>
      clientInstance.get(`reports/doors/`, {
        params,
        signal,
      })
    )
  }

  async function getChainsWithCRMReports(params = {}, signal?: AbortSignal) {
    return baseAPICall<PaginatedResponse<ChainProxyReport>>(() =>
      clientInstance.get(`reports/chains/`, {
        params,
        signal,
      })
    )
  }

  async function getChainReportsSummary(params = {}, signal?: AbortSignal) {
    return baseAPICall<ChainsSummary>(() =>
      clientInstance.get('reports/chains/summary/', {
        params,
        signal,
        transformResponse: (data: string) => JSON.parse(data),
      })
    )
  }

  const useGetChainsWithCRMReports = (
    params = {},
    signal?: AbortSignal,
    enabled = true
  ) =>
    useQuery({
      queryKey: [CHAIN_SUMMARY_REPORTS_KEY, params],
      queryFn: () => {
        return getChainsWithCRMReports({ ...params }, signal)
      },
      enabled,
    })

  const useGetChainReportsSummary = (
    params = {},
    signal?: AbortSignal,
    enabled = true
  ) =>
    useQuery({
      queryKey: [CHAIN_SUMMARY_KEY, params],
      queryFn: () => {
        return getChainReportsSummary({ ...params }, signal)
      },
      enabled,
    })

  async function getDoorReportsSummary(params = {}, signal?: AbortSignal) {
    return baseAPICall<{ results: PlacesSummary[] }>(() =>
      clientInstance.get('reports/doors/summary/', {
        params,
        signal,
        transformResponse: (data: string) => JSON.parse(data),
      })
    )
  }

  const useGetDoorReportsSummary = (
    params = {},
    signal?: AbortSignal,
    enabled = true
  ) =>
    useQuery({
      queryKey: [DOOR_SUMMARY_REPORTS_KEY, params],
      queryFn: async () => {
        const res = await getDoorReportsSummary({ ...params }, signal)
        return (
          res?.results?.[0] ?? {
            total_ltv: 0,
            chains_count: 0,
            places_count: 0,
          }
        )
      },
      enabled,
    })

  async function getDoorsForDealSelection(
    chainProxyId: number,
    params = {},
    body: {
      ids?: number[]
      exclude_ids?: number[]
    },
    signal?: AbortSignal
  ) {
    return baseAPICall<PaginatedResponse<DoorProxyForSelection>>(() =>
      clientInstance.post(
        `contacts/contactcompanies/${chainProxyId}/doors-for-selection/`,
        body,
        {
          params,
          signal,
        }
      )
    )
  }

  async function getDoorsForSelectionIdsOnly(
    chainProxyId: number,
    params = {},
    body: {
      ids?: number[]
      exclude_ids?: number[]
    },
    signal?: AbortSignal
  ) {
    return baseAPICall<{ ids: number[] }>(() =>
      clientInstance.post(
        `contacts/contactcompanies/${chainProxyId}/doors-for-selection/ids-only/`,
        body,
        {
          params,
          signal,
        }
      )
    )
  }

  async function getDoorsForSelectionCount(
    chainProxyId: number,
    signal?: AbortSignal
  ) {
    return baseAPICall<{ count: number }>(() =>
      clientInstance.get(
        `contacts/contactcompanies/${chainProxyId}/doors-for-selection/count/`,
        {
          signal,
        }
      )
    )
  }

  function useGetDoorsForSelectionCount(
    chainProxyId: number,
    signal?: AbortSignal,
    enabled = true
  ) {
    return useQuery({
      queryKey: ['doors-for-selection-count', chainProxyId],
      queryFn: () => getDoorsForSelectionCount(chainProxyId, signal),
      refetchOnMount: false,
      enabled,
    })
  }

  async function getNotes(
    chain_id: number | undefined,
    contact_company_id: number | undefined,
    params = {},
    signal?: AbortSignal,
    door_proxy_id?: number,
    noteType?: 'company' | 'door'
  ) {
    const apiParams: Record<string, any> = {
      ...params,
      chain_id,
      contact_company_id,
    }

    if (door_proxy_id) {
      apiParams.door_proxy_id = door_proxy_id
    }

    if (noteType) {
      apiParams.note_type = noteType
    }

    return baseAPICall<PaginatedResponse<INote>>(() =>
      clientInstance.get(`contacts/contactcompanies/notes/`, {
        params: apiParams,
        signal,
      })
    )
  }

  async function editNote(id: number, content: string) {
    return baseAPICall(() =>
      clientInstance.patch(`contacts/contactcompanies/notes/${id}/`, {
        content: content,
      })
    )
  }

  async function createNote(
    chain_id: number | undefined,
    contact_company_id: number | undefined,
    content?: string,
    attachments?: File[],
    door_proxy_id?: number
  ): Promise<INote> {
    return baseAPICall(() => {
      const formData = new FormData()
      formData.append('content', content || '')

      if (chain_id) {
        formData.append('chain_id', chain_id.toString())
      }

      if (contact_company_id) {
        formData.append('contact_company_id', contact_company_id.toString())
      }

      if (door_proxy_id) {
        formData.append('door_proxy_id', door_proxy_id.toString())
      }

      if (attachments) {
        attachments.forEach((file) => {
          formData.append('attachments', file)
        })
      }

      return clientInstance.post(`contacts/contactcompanies/notes/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    })
  }

  async function deleteNote(id: number) {
    return baseAPICall(() =>
      clientInstance.delete(`contacts/contactcompanies/notes/${id}/`)
    )
  }

  async function getDealNotes(
    dealId: number,
    params: Record<string, any> = {}
  ): Promise<PaginatedResponse<INote>> {
    return baseAPICall<PaginatedResponse<INote>>(() =>
      clientInstance.get(`contacts/deals/${dealId}/notes/`, { params })
    )
  }

  async function createDealNote(
    dealId: number,
    content: string,
    attachments?: File[]
  ): Promise<INote> {
    return baseAPICall(() => {
      const formData = new FormData()
      formData.append('content', content)

      if (attachments) {
        attachments.forEach((file) => {
          formData.append('attachments', file)
        })
      }

      return clientInstance.post(`contacts/deals/${dealId}/notes/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    })
  }

  async function editDealNote(
    dealId: number,
    noteId: number,
    content: string,
    attachmentsToDelete?: number[],
    newAttachments?: File[]
  ): Promise<INote> {
    const hasFiles = newAttachments && newAttachments.length > 0
    const hasDeletes = attachmentsToDelete && attachmentsToDelete.length > 0

    if (hasFiles || hasDeletes) {
      const formData = new FormData()
      formData.append('content', content)
      if (hasDeletes) {
        attachmentsToDelete.forEach((id) =>
          formData.append('attachments_to_delete', String(id))
        )
      }
      if (hasFiles) {
        newAttachments.forEach((file) => formData.append('attachments', file))
      }
      return baseAPICall(() =>
        clientInstance.patch(
          `contacts/deals/${dealId}/notes/${noteId}/`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
      )
    }

    return baseAPICall(() =>
      clientInstance.patch(`contacts/deals/${dealId}/notes/${noteId}/`, {
        content,
      })
    )
  }

  async function deleteDealNote(dealId: number, noteId: number): Promise<void> {
    return baseAPICall(() =>
      clientInstance.delete(`contacts/deals/${dealId}/notes/${noteId}/`)
    )
  }

  const useGetDeals = (params = {}) => {
    const queryKey = ['deals', JSON.stringify(params)]
    return [
      useQuery({
        queryKey: queryKey,
        queryFn: () => getDeals(params),
      }),
      queryKey,
    ] as const
  }

  const useGetDealsCounts = (params = {}) =>
    useQuery({
      queryKey: ['deals-counts', params],
      queryFn: () => getDealsCounts(params),
    })

  const useGetDealsInfinite = (params = {}) =>
    useInfiniteQuery({
      initialPageParam: 1,
      queryKey: ['deals', params],
      queryFn: ({ pageParam = 1 }) => getDeals({ ...params, page: pageParam }),
      getNextPageParam: (lastPage) => {
        // must return "undefined" when there is no more data
        // return lastPage.hasMore ? lastPage.nextPage : undefined
        return lastPage.page < lastPage.total_pages
          ? lastPage.page + 1
          : undefined
      },
    })

  const useGetReportDeals = (params = {}) =>
    useQuery({
      queryKey: ['report-deals', JSON.stringify(params)],
      queryFn: () => getReportDeals(params),
    })

  const useGetReportDealsInfinite = (
    params: { page_size: number } & Record<string, unknown>
  ) => {
    const queryKey: string[] = ['report-deals', JSON.stringify(params)]
    return [
      useInfiniteQuery({
        initialPageParam: 1,
        queryKey: queryKey,
        queryFn: ({ pageParam = 1 }) =>
          getReportDeals({ ...params, page: pageParam }),
        getNextPageParam: (lastPage, allPages) => {
          const totalFetched = allPages.reduce(
            (sum, page) => sum + page.results.length,
            0
          )
          return totalFetched < lastPage.count ? allPages.length + 1 : undefined
        },
      }),
      queryKey,
    ] as const
  }

  const editDeal = async (dealId: number, data: Partial<DealEdit>) => {
    const formData = new FormData()
    if (data.name !== undefined) {
      formData.append('name', data.name || '')
    }
    if (data.product_id) {
      formData.append('product_id', data.product_id.toString())
    }
    if (data.product_variant_id !== undefined) {
      formData.append(
        'product_variant_id',
        data.product_variant_id?.toString() || ''
      )
    }
    if (data.sales_stage) {
      formData.append('sales_stage', data.sales_stage.toString())
    }
    if (data.account_owner) {
      formData.append('account_owner', data.account_owner.toString())
    }
    if (data.monthly_volume_override !== undefined) {
      formData.append(
        'monthly_volume_override',
        data.monthly_volume_override?.toString() ?? ''
      )
    }
    if (data.monthly_revenue_override !== undefined) {
      formData.append(
        'monthly_revenue_override',
        data.monthly_revenue_override?.toString() ?? ''
      )
    }
    if (data.close_date_override !== undefined) {
      formData.append('close_date_override', data.close_date_override ?? '')
    }
    if (data.notes) {
      formData.append('notes', data.notes)
    }
    if (data.origin_campaign_id !== undefined) {
      formData.append('origin_campaign_id', data.origin_campaign_id || '')
    }
    if (data.attachments) {
      data.attachments.forEach((file) => {
        formData.append('attachments', file)
      })
    }
    if (data.apply_to) {
      formData.append('apply_to', data.apply_to)
    }
    if (data.door_ids) {
      data.door_ids.forEach((id) => formData.append('door_ids', id.toString()))
    }

    if (data.distributors) {
      if (Array.isArray(data.distributors)) {
        data.distributors.forEach((id) =>
          formData.append('distributors', id.toString())
        )
      } else {
        formData.append('distributors', data.distributors)
      }
    }

    if (data.tags) {
      if (Array.isArray(data.tags)) {
        data.tags.forEach((id) => formData.append('tags', id.toString()))
      } else {
        formData.append('tags', data.tags)
      }
    }

    return baseAPICall(() =>
      clientInstance.patch(`contacts/deals/${dealId}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    )
  }

  async function deleteDeal(id: number) {
    return baseAPICall(() => clientInstance.delete(`contacts/deals/${id}/`))
  }

  async function manageDealRelatedCompanies(
    dealId: number,
    contactCompanyIds: number[],
    action: 'add' | 'remove'
  ) {
    return baseAPICall(() =>
      clientInstance.post(`contacts/deals/${dealId}/related-companies/`, {
        contact_company_ids: contactCompanyIds,
        action,
      })
    )
  }

  async function getSelectedDoorIdsForDeal(dealId: number) {
    return baseAPICall<{ ids: number[] }>(() =>
      clientInstance.get(`contacts/deals/${dealId}/selected-door-ids/`)
    )
  }

  const useGetSelectedDoorIdsForDeal = (
    dealId: number | undefined,
    enabled: boolean = true,
    options?: { noCache?: boolean }
  ) =>
    useQuery({
      queryKey: ['deal-door-ids', dealId],
      queryFn: () => getSelectedDoorIdsForDeal(dealId!),
      enabled: enabled && !!dealId,
      ...(options?.noCache && {
        refetchOnMount: 'always' as const,
        staleTime: 0,
        gcTime: 0,
      }),
    })

  async function getDeal(dealId: number) {
    return baseAPICall<DealDetail>(
      () => clientInstance.get(`contacts/deals/${dealId}/`),
      { silentStatuses: [404] }
    )
  }

  const useGetDeal = (
    dealId: number | undefined,
    enabled: boolean = true,
    options?: { noCache?: boolean }
  ) =>
    useQuery({
      queryKey: ['deal', String(dealId)],
      queryFn: () => getDeal(dealId!),
      enabled: enabled && !!dealId,
      retry: false,
      ...(options?.noCache && {
        refetchOnMount: 'always' as const,
        staleTime: 0,
        gcTime: 0,
      }),
    })

  const useProducts = (params?: Record<string, any>, disabled?: boolean) =>
    useQuery({
      queryKey: ['products', params],
      queryFn: async () => {
        return await getProducts(params)
      },
      refetchOnMount: false,
      enabled: !disabled,
    })

  const deleteCompany = async (id: number) => {
    return baseAPICall(() =>
      clientInstance.delete(`contacts/contactcompanies/${id}/`)
    )
  }

  const getDealHistory = async (dealId: number): Promise<DealHistory[]> => {
    return baseAPICall<DealHistory[]>(() =>
      clientInstance.get(`contacts/deals/${dealId}/history/`)
    )
  }

  const useGetDealHistory = ({ dealId }: { dealId: number }) => {
    return useQuery({
      queryKey: ['deal_history', dealId],
      queryFn: () => getDealHistory(dealId),
    })
  }

  async function getPreferences() {
    return baseAPICall<Preferences>(() => clientInstance.get('preferences/'))
  }

  const useGetPreferences = (disabled: boolean = isLimited) =>
    useQuery({
      queryKey: ['preferences'],
      queryFn: getPreferences,
      refetchOnMount: false,
      enabled: !disabled,
    })

  async function getUnreadNotificationsCount() {
    return baseAPICall<{
      count: number
    }>(() => clientInstance.get('notifications/unread-count/'))
  }

  const useGetUnreadNotificationsCount = () =>
    useQuery({
      queryKey: ['unread-notifications'],
      queryFn: getUnreadNotificationsCount,
      refetchOnMount: true,
      refetchInterval: 10000,
    })

  async function getNotifications({ unread }: { unread?: boolean } = {}) {
    return baseAPICall<Notification[]>(() =>
      clientInstance.get('notifications/', {
        params: {
          is_read: !unread,
        },
      })
    )
  }

  const useGetNotifications = ({ unread }: { unread?: boolean } = {}) =>
    useQuery({
      queryKey: ['notifications', unread],
      queryFn: () => getNotifications({ unread }),
      refetchOnMount: true,
    })

  async function markNotificationAsRead(ids: number[]) {
    return baseAPICall<Notification>(() =>
      clientInstance.post(`notifications/mark-as-read/`, {
        notification_ids: ids,
      })
    )
  }

  async function markAllNotificationsAsRead() {
    return baseAPICall<Notification>(() =>
      clientInstance.post(`notifications/mark-all-as-read/`)
    )
  }

  function getDashboardOverviewV2() {
    return baseAPICall(() =>
      clientInstance.get('reports/dashboard/overview/')
    ) as Promise<
      {
        title: string
        value: string
        tooltip: string
      }[]
    >
  }

  function getDashboardSalesForecastV2(
    timeHorizon = 12,
    accountOwnerIds?: number[],
    productIds?: number[],
    variantIds?: number[],
    stageIds?: number[],
    subCatIds?: number[],
    campaignIds?: number[],
    distributorIds?: number[],
    gpoIds?: number[],
    taglistIds?: number[]
  ) {
    return baseAPICall(() =>
      clientInstance.get('reports/dashboard/sales-forecast/', {
        params: {
          time_horizon: timeHorizon,
          account_owner_ids: accountOwnerIds,
          product_ids: productIds,
          variant_ids: variantIds,
          stage_ids: stageIds,
          sub_category_ids: subCatIds,
          campaign_ids: campaignIds,
          distributor_ids: distributorIds,
          gpo_ids: gpoIds,
          taglist_ids: taglistIds,
        },
      })
    ) as Promise<{
      charts: {
        [key: string]: {
          labels: string[]
          values: number[]
        }
      }
      cards: { label: string; value: string; tooltip?: string }[]
    }>
  }

  function getDashboardOverview() {
    return baseAPICall(() =>
      clientInstance.get('products/dashboard/overview/')
    ) as Promise<
      {
        title: string
        value: string
        tooltip: string
      }[]
    >
  }

  const useGetDashboardOverview = (useV2: boolean) => {
    return useQuery({
      queryKey: [useV2 ? 'dashboard_overview_v2' : 'dashboard_overview'],
      queryFn: useV2 ? getDashboardOverviewV2 : getDashboardOverview,
    })
  }

  function getDashboardSalesForecast(
    timeHorizon = 12,
    accountOwnerIds?: number[],
    productIds?: number[],
    variantIds?: number[],
    stageIds?: number[],
    subCatIds?: number[],
    campaignIds?: number[],
    distributorIds?: number[],
    gpoIds?: number[],
    taglistIds?: number[]
  ) {
    return baseAPICall(() =>
      clientInstance.get('products/dashboard/sales_forecast/', {
        params: {
          time_horizon: timeHorizon,
          account_owner_ids: accountOwnerIds,
          product_ids: productIds,
          variant_ids: variantIds,
          stage_ids: stageIds,
          sub_category_ids: subCatIds,
          campaign_ids: campaignIds,
          distributor_ids: distributorIds,
          gpo_ids: gpoIds,
          taglist_ids: taglistIds,
        },
      })
    ) as Promise<{
      charts: {
        [key: string]: {
          labels: string[]
          values: number[]
        }
      }
      cards: { label: string; value: string; tooltip?: string }[]
    }>
  }

  const useGetDashboardSalesForecast = (
    useV2: boolean,
    timeHorizon = 12,
    accountOwnerIds?: number[],
    productIds?: number[],
    variantIds?: number[],
    stageIds?: number[],
    subCatIds?: number[],
    campaignIds?: number[],
    distributorIds?: number[],
    gpoIds?: number[],
    taglistIds?: number[]
  ) => {
    return useQuery({
      queryKey: [
        useV2 ? 'sales_forecast_v2' : 'sales_forecast',
        timeHorizon,
        accountOwnerIds,
        productIds,
        variantIds,
        stageIds,
        subCatIds,
        campaignIds,
        distributorIds,
        gpoIds,
        taglistIds,
      ],
      queryFn: () =>
        useV2
          ? getDashboardSalesForecastV2(
              timeHorizon,
              accountOwnerIds,
              productIds,
              variantIds,
              stageIds,
              subCatIds,
              campaignIds,
              distributorIds,
              gpoIds,
              taglistIds
            )
          : getDashboardSalesForecast(
              timeHorizon,
              accountOwnerIds,
              productIds,
              variantIds,
              stageIds,
              subCatIds,
              campaignIds,
              distributorIds,
              gpoIds,
              taglistIds
            ),
    })
  }

  async function saveFilters(payload: SavedViewCreate) {
    return baseAPICall<Notification[]>(
      () =>
        clientInstance.post('saved-views/', {
          ...payload,
        }),
      { disableNotifyError: true, disableNotifyGenericError: true }
    )
  }

  async function deleteFilter(filterId: number) {
    return baseAPICall(
      () => clientInstance.delete(`saved-views/${filterId}/`),
      {
        disableNotifyError: true,
      }
    )
  }

  async function updateFilter(filterId: number, payload: SavedViewUpdate) {
    return baseAPICall<SavedView>(
      () => clientInstance.patch(`saved-views/${filterId}/`, payload),
      { disableNotifyError: true, disableNotifyGenericError: true }
    )
  }

  async function getSavedFiltersOptions(identifier: FilterIdentifier) {
    return baseAPICall<
      {
        label: string
        value: number
        original: SavedView
      }[]
    >(() =>
      clientInstance.get('saved-views/', {
        params: {
          identifier,
        },
        transformResponse: (data) => {
          const savedViews = JSON.parse(data)
          return savedViews.map((savedView: SavedView) => ({
            label: savedView.name,
            value: savedView.id,
            original: savedView,
          }))
        },
      })
    )
  }

  async function getAllSavedFilters() {
    return baseAPICall<SavedView[]>(() => clientInstance.get('saved-views/'))
  }

  const useGetAllSavedFilters = () => {
    return useQuery({
      queryKey: ['saved-views/'],
      queryFn: () => getAllSavedFilters(),
    })
  }

  const useGetSavedFiltersOptions = (identifier: FilterIdentifier) => {
    return useQuery({
      queryKey: ['saved_filters', identifier],
      queryFn: () => getSavedFiltersOptions(identifier),
      refetchOnMount: true,
      staleTime: 1000 * 60 * 5,
    })
  }

  const useGetCompanyDetails = () => {
    return useQuery({
      queryKey: ['company-details'],
      queryFn: () => getCompanyDetails(),
    })
  }

  const useGetCompanyDomainRequests = () => {
    return useQuery({
      queryKey: ['company/email-extension-request/'],
      queryFn: () => getCompanyDomainRequests(),
    })
  }

  const useGetCompanyEmailExtensions = () => {
    return useQuery({
      queryKey: ['company/email-extensions/'],
      queryFn: () => getCompanyEmailExtensions(),
    })
  }

  async function postCompanyDomain(
    values: Pick<CompanyEmailExtensionRequests, 'email_extension' | 'note'>
  ) {
    return baseAPICall<Product>(
      () =>
        clientInstance.post(
          'products/company/email-extension-request/',
          values
        ),
      {
        successMessage: 'Company domain created successfully',
      }
    )
  }

  async function cancelCompanyDomainRequest(id: number) {
    return baseAPICall(() =>
      clientInstance.post(
        `products/company/email-extension-request/${id}/cancel/`,
        {}
      )
    )
  }

  const postSuggestion = async (data: SuggestionCreate) => {
    return baseAPICall(() =>
      clientInstance.post('restaurants/suggestions/', data)
    )
  }

  async function getDoorProxiesOfChainProxy(chainProxyId: number, params = {}) {
    return baseAPICall<StandardPaginatedResponse<IDoorProxy>>(() =>
      clientInstance.get(`contacts/contactcompanies/${chainProxyId}/doors/`, {
        params,
      })
    )
  }

  async function getDoorProxiesOfChainProxySummary(
    chainProxyId: number,
    params = {}
  ) {
    return baseAPICall<StandardSummaryResponse>(() =>
      clientInstance.get(
        `contacts/contactcompanies/${chainProxyId}/doors/summary/`,
        {
          params,
        }
      )
    )
  }

  const useDoorProxiesOfChainProxy = (chainProxyId?: number) =>
    useQuery({
      queryKey: ['door_proxies_of_chain_proxy', chainProxyId],
      queryFn: () => getDoorProxiesOfChainProxy(chainProxyId ?? -1),
      refetchOnMount: false,
      enabled: !!chainProxyId,
    })

  function deleteDoorProxy(doorProxyId: number) {
    return clientInstance.delete(`contacts/doorproxies/${doorProxyId}/`)
  }

  async function getUnmatchedCompanyCities(
    subCategoryId: number,
    signal?: AbortSignal
  ) {
    return baseAPICall(() =>
      clientInstance.get(`contacts/contactcompanies/cities/`, {
        signal,
        params: { sub_category_id: subCategoryId },
      })
    )
  }

  const useGetUnmatchedCompanyCities = (subCategoryId: number) =>
    useQuery({
      queryKey: ['unmatched_company_cities', subCategoryId],
      queryFn: () => getUnmatchedCompanyCities(subCategoryId),
      refetchOnMount: false,
      enabled: !!subCategoryId,
    })

  async function getDoorProxyCities(
    contactCompanyId?: number,
    subCategoryId?: number,
    unmatched: boolean = false,
    signal?: AbortSignal
  ) {
    if (!contactCompanyId && !subCategoryId) {
      throw new Error(
        'Either contactCompanyId or subCategoryId must be provided'
      )
    }
    return baseAPICall(() =>
      clientInstance.get(`contacts/doorproxies/cities/`, {
        signal,
        params: {
          contact_company_id: contactCompanyId,
          sub_category_id: subCategoryId,
          unmatched: unmatched,
        },
      })
    )
  }

  const useGetDoorProxyCities = ({
    contactCompanyId,
    subCategoryId,
    unmatched = false,
  }: {
    contactCompanyId?: number
    subCategoryId?: number
    unmatched?: boolean
  }) =>
    useQuery({
      queryKey: [
        'door_proxy_cities',
        contactCompanyId,
        subCategoryId,
        unmatched,
      ],
      queryFn: () =>
        getDoorProxyCities(contactCompanyId, subCategoryId, unmatched),
      refetchOnMount: false,
      enabled: !!contactCompanyId || !!subCategoryId,
    })

  async function getDoorProxyStates(
    contactCompanyId?: number,
    subCategoryId?: number,
    unmatched: boolean = false,
    signal?: AbortSignal
  ) {
    if (!contactCompanyId && !subCategoryId) {
      throw new Error(
        'Either contactCompanyId or subCategoryId must be provided'
      )
    }
    return baseAPICall(() =>
      clientInstance.get(`contacts/doorproxies/states/`, {
        signal,
        params: {
          contact_company_id: contactCompanyId,
          sub_category_id: subCategoryId,
          unmatched: unmatched,
        },
      })
    )
  }

  const useGetDoorProxyStates = ({
    contactCompanyId,
    subCategoryId,
    unmatched = false,
  }: {
    contactCompanyId?: number
    subCategoryId?: number
    unmatched?: boolean
  }) =>
    useQuery({
      queryKey: [
        'door_proxy_states',
        contactCompanyId,
        subCategoryId,
        unmatched,
      ],
      queryFn: () =>
        getDoorProxyStates(contactCompanyId, subCategoryId, unmatched),
      refetchOnMount: false,
      enabled: !!contactCompanyId || !!subCategoryId,
    })

  type ChangeMiscSettingsRequest = {
    key: MISC_SETTINGS_TYPES
    value: unknown
  }

  function changeMiscSettings(args: ChangeMiscSettingsRequest) {
    return baseAPICall(() => {
      return clientInstance.post(
        `preferences/misc-settings/update/${args.key}/`,
        {
          key: args.key,
          value: args.value,
        }
      )
    })
  }

  const useChangeMiscSettings = () => {
    return useMutation({
      mutationFn: (variables: ChangeMiscSettingsRequest) =>
        changeMiscSettings(variables),
      onMutate: async (newSetting) => {
        const prev = queryClient.getQueriesData({
          queryKey: ['preferences'],
        })

        queryClient.setQueriesData(
          { queryKey: ['preferences'] },
          (old?: Preferences) => {
            if (!old) {
              return
            }
            let data = structuredClone(old)
            const miscSettings = data.misc_settings.filter(
              (it) => it.key !== newSetting.key
            )
            miscSettings.push({ key: newSetting.key, value: newSetting.value })
            data = {
              ...data,
              misc_settings: miscSettings,
            }
            return data
          }
        )

        return { prev }
      },
      onError: (_, __, context) => {
        queryClient.setQueryData(['preferences'], context?.prev)
        queryClient.setQueriesData(
          {
            queryKey: ['preferences'],
          },
          context?.prev
        )
      },
    })
  }

  const addAttachmentsToNote = async (
    noteId: number,
    attachments: File[]
  ): Promise<INote> => {
    const formData = new FormData()
    attachments.forEach((file) => {
      formData.append('attachments', file)
    })

    return baseAPICall(() =>
      clientInstance.post(
        `contacts/contactcompanies/notes/${noteId}/attachments/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
    )
  }

  const deleteNoteAttachment = async (noteId: number, attachmentId: number) => {
    return baseAPICall(() =>
      clientInstance.delete(
        `contacts/contactcompanies/notes/${noteId}/attachments/${attachmentId}/`
      )
    )
  }

  const deleteAutomationAttachment = async (
    automationId: number,
    attachmentId: number
  ) => {
    return baseAPICall(() =>
      clientInstance.delete(
        `automations/${automationId}/attachments/${attachmentId}/`
      )
    )
  }

  const getAsyncJobs = async (params = {}) => {
    return baseAPICall<PaginatedResponse<AsyncJobListResponse>>(() =>
      clientInstance.get('async-jobs/', {
        params,
      })
    )
  }

  const useGetAsyncJobs = (params = {}) =>
    useQuery({
      queryKey: ['async-jobs', JSON.stringify(params)],
      queryFn: () => getAsyncJobs(params),
    })

  const contactStarToggle = async (contactId: number) => {
    return baseAPICall<boolean>(() =>
      clientInstance.patch(`contacts/startoggle/${contactId}/`)
    )
  }

  function bulkDeleteDeals(
    dealIDs: number[],
    allRowsSelected: boolean,
    filterParams: any
  ) {
    const endpoint = `contacts/deals/bulk/delete/`

    return baseAPICall(() =>
      clientInstance.post(
        endpoint,
        {
          include_ids: allRowsSelected ? [] : dealIDs,
          exclude_ids: allRowsSelected ? dealIDs : [],
        },
        {
          params: filterParams,
        }
      )
    )
  }

  function bulkDeleteDealReports(
    dealIDs: number[],
    allRowsSelected: boolean,
    filterParams: any
  ) {
    const endpoint = `reports/deals/bulk/delete/`

    return baseAPICall(() =>
      clientInstance.post(
        endpoint,
        {
          include_ids: allRowsSelected ? [] : dealIDs,
          exclude_ids: allRowsSelected ? dealIDs : [],
        },
        {
          params: filterParams,
        }
      )
    )
  }
  const inviteUser = async (
    email: string,
    userType?: string,
    rsmAssignmentId?: number | null
  ) => {
    return baseAPICall<boolean>(
      () =>
        clientInstance.post(`invite-user/`, {
          email,
          user_type: userType ?? 'Standard',
          rsm_assignment_id: rsmAssignmentId ?? null,
        }),
      {
        disableNotifyGenericError: false,
      }
    )
  }

  async function checkInviteToken(token: string, email: string) {
    return baseAPICall<void>(
      () => clientInstance.post('auth/check-invite/', { token, email }),
      { disableNotifyGenericError: true, disableNotifyError: true }
    )
  }

  async function listInvitations(params?: {
    page?: number
    page_size?: number
    search?: string
  }) {
    return baseAPICall<{
      count: number
      results: Invitation[]
    }>(() => clientInstance.get('invitations/', { params }))
  }

  async function resendInvitation(id: number) {
    return baseAPICall<void>(() =>
      clientInstance.post(`invitations/${id}/resend/`)
    )
  }

  async function getStripePortalRedirect(returnUrl: string) {
    return baseAPICall<string>(() =>
      clientInstance.get('products/stripe-portal-redirect/', {
        params: { return_url: returnUrl },
      })
    )
  }

  async function getCompanyDetails() {
    return baseAPICall(() =>
      clientInstance.get<CompanyDetailsData>('products/company-details/')
    )
  }

  async function getCompanyDomainRequests() {
    return baseAPICall(() =>
      clientInstance.get('products/company/email-extension-request/')
    )
  }

  async function getCompanyEmailExtensions() {
    return baseAPICall(() =>
      clientInstance.get('products/company/email-extensions/')
    )
  }

  // Company Logo Management Functions
  async function getCompanyLogos() {
    return baseAPICall<CompanyLogo[]>(() =>
      clientInstance.get('products/company/logos/')
    )
  }

  async function createCompanyLogo(logo?: File) {
    const formData = new FormData()

    if (logo) {
      formData.append('logo', logo)
    }

    return baseAPICall<CompanyLogo>(
      () =>
        clientInstance.post('products/company/logos/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
      { successMessage: 'Logo uploaded successfully' }
    )
  }

  async function getCompanyLogo(logoId: number) {
    return baseAPICall<CompanyLogo>(() =>
      clientInstance.get(`products/company/logos/${logoId}/`)
    )
  }

  async function updateCompanyLogo(logoId: number, logo?: File) {
    const formData = new FormData()

    if (logo) {
      console.log('type', typeof logo)
      formData.append('logo', logo)
    }

    return baseAPICall<CompanyLogo>(
      () =>
        clientInstance.put(`products/company/logos/${logoId}/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
      { successMessage: 'Logo updated successfully' }
    )
  }

  async function deleteCompanyLogo(logoId: number) {
    return baseAPICall(
      () => clientInstance.delete(`products/company/logos/${logoId}/`),
      { successMessage: 'Logo deleted successfully' }
    )
  }

  async function setCompanyLogoPrimary(logoId: number) {
    return baseAPICall<CompanyLogo>(
      () =>
        clientInstance.post(`products/company/logos/${logoId}/set-primary/`),
      { successMessage: 'Primary logo updated successfully' }
    )
  }

  // React Query hooks for company logos
  const useGetCompanyLogos = () => {
    return useQuery({
      queryKey: ['company/logos/'],
      queryFn: () => getCompanyLogos(),
      refetchOnMount: false,
    })
  }

  const useGetCompanyLogo = (logoId?: number) => {
    return useQuery({
      queryKey: ['company/logos/', logoId],
      queryFn: () => getCompanyLogo(logoId!),
      enabled: !!logoId,
    })
  }

  // Company Logo Mutations
  const useCreateCompanyLogo = () => {
    return useMutation({
      mutationFn: createCompanyLogo,
    })
  }

  const useUpdateCompanyLogo = () => {
    return useMutation({
      mutationFn: ({ logoId, logo }: { logoId: number; logo?: File }) =>
        updateCompanyLogo(logoId, logo),
    })
  }

  const useDeleteCompanyLogo = () => {
    return useMutation({
      mutationFn: deleteCompanyLogo,
    })
  }

  const useSetCompanyLogoPrimary = () => {
    return useMutation({
      mutationFn: setCompanyLogoPrimary,
    })
  }

  async function updateCompanyDetails({
    billingEmail,
    rebateSupportEmail,
    sampleCustomerServiceEmail,
  }: {
    billingEmail: string
    rebateSupportEmail: string
    sampleCustomerServiceEmail?: string
  }) {
    return baseAPICall(
      () =>
        clientInstance.put('products/company-details/', {
          billing_email: billingEmail,
          rebate_support_email: rebateSupportEmail,
          sample_customer_service_email: sampleCustomerServiceEmail,
        }),
      { successMessage: 'Company details updated successfully' }
    )
  }

  async function getTextContent() {
    return baseAPICall(() => clientInstance.get('common/text-content/'))
  }

  const useGetTextContent = () =>
    useQuery({
      queryKey: ['text-content'],
      queryFn: () => getTextContent(),
    })

  type DataRequestSubmission = {
    first_name: string
    last_name: string
    email: string
    data_request_type: string
  }

  // Reminders API
  async function getReminders(params = {}, signal?: AbortSignal) {
    return baseAPICall<PaginatedResponse<ReminderListItem>>(() =>
      clientInstance.get('reminders/reminders/', { params, signal })
    )
  }

  async function createReminder(data: ReminderCreatePayload) {
    return baseAPICall(() => clientInstance.post('reminders/reminders/', data))
  }

  async function getReminder(reminderId: number, signal?: AbortSignal) {
    return baseAPICall<ReminderDetailItem>(() =>
      clientInstance.get(`reminders/reminders/${reminderId}/`, { signal })
    )
  }

  async function updateReminder(
    reminderId: number,
    data: ReminderUpdatePayload
  ) {
    return baseAPICall(() =>
      clientInstance.patch(`reminders/reminders/${reminderId}/`, data)
    )
  }

  async function deleteReminder(reminderId: number) {
    return baseAPICall(() =>
      clientInstance.delete(`reminders/reminders/${reminderId}/`)
    )
  }

  async function pauseReminder(reminderId: number) {
    return baseAPICall<{ message: string; status: string }>(() =>
      clientInstance.post(`reminders/reminders/${reminderId}/pause/`)
    )
  }

  async function unpauseReminder(reminderId: number) {
    return baseAPICall<{ message: string; status: string }>(() =>
      clientInstance.post(`reminders/reminders/${reminderId}/unpause/`)
    )
  }

  // For backward compatibility - now actually deletes
  async function cancelReminder(reminderId: number) {
    return deleteReminder(reminderId)
  }

  async function dataRequestSubmission(data: DataRequestSubmission) {
    return baseAPICall(() =>
      clientInstance.post(`common/data-request-submission/`, data)
    )
  }

  type OptOutSubmission = {
    first_name: string
    last_name: string
    email: string
    do_not_sell: boolean
  }

  async function optOutSubmission(data: OptOutSubmission) {
    return baseAPICall(() =>
      clientInstance.post(`common/opt-out-submission/`, data)
    )
  }

  const fetchCreditBalance = async (): Promise<CreditBalanceResponse> => {
    return baseAPICall<CreditBalanceResponse>(() =>
      clientInstance.get('contact_request/credits/')
    )
  }

  async function purchaseCredits(data: {
    amount: number
    payment_method_id: string
  }) {
    return baseAPICall<{
      message: string
      transaction: {
        payment_intent_id: string
        credit_grant_id: string
        amount: number
      }
      credits: {
        total: number
        on_hold: number
        available: number
      }
    }>(() => clientInstance.post('contact_request/credits/purchase/', data))
  }

  async function createCreditCheckoutSession(data: {
    amount: number
    success_url?: string
    cancel_url?: string
  }) {
    return baseAPICall<{
      session_id: string
      url: string
    }>(() => clientInstance.post('contact_request/credits/checkout/', data))
  }

  async function getDoorProxy(doorProxyId?: number): Promise<IDoorProxy> {
    return baseAPICall(() =>
      clientInstance.get(`contacts/doorproxies/${doorProxyId}/`)
    )
  }

  const useGetDoorProxy = (doorProxyId?: number) =>
    useQuery<IDoorProxy, AxiosError>({
      queryKey: ['door_proxy', doorProxyId],
      queryFn: () => getDoorProxy(doorProxyId),
      enabled: !!doorProxyId,
      retry: (failureCount, error: AxiosError) => {
        return axios.isAxiosError(error) && error?.response?.status === 404
          ? false
          : failureCount < 3
      },
    })

  async function getAllDoorProxies(params = {}) {
    return baseAPICall<StandardPaginatedResponse<IDoorProxy>>(() =>
      clientInstance.get(`contacts/doorproxies/`, {
        params,
      })
    )
  }

  async function getAllDoorProxiesSummary(params = {}) {
    // Use the summary endpoint directly
    return baseAPICall<StandardSummaryResponse>(() =>
      clientInstance.get(`contacts/doorproxies/summary/`, {
        params,
      })
    )
  }

  async function getNotificationsPreferencesByKind(kindSlug: string) {
    return baseAPICall<UserNotificationSettingSummary[]>(() =>
      clientInstance.get(`notifications/preferences/kind/${kindSlug}/`)
    )
  }

  const useGetNotificationsPreferencesByKind = (kindSlug: string) => {
    return useQuery({
      queryKey: ['notifications-preferences', kindSlug],
      queryFn: () => getNotificationsPreferencesByKind(kindSlug),
    })
  }

  async function updateAllNotificationsPreferencesByKind(
    kindSlug: string,
    data: {
      emailEnabled?: boolean
      inAppEnabled?: boolean
    }
  ) {
    return baseAPICall(() =>
      clientInstance.patch(`notifications/preferences/kind/${kindSlug}/`, {
        email_enabled: data.emailEnabled,
        in_app_enabled: data.inAppEnabled,
      })
    )
  }

  function useUpdateAllNotificationsPreferencesByKind() {
    return useMutation({
      mutationFn: (data: {
        kindSlug: string
        emailEnabled?: boolean
        inAppEnabled?: boolean
      }) => updateAllNotificationsPreferencesByKind(data.kindSlug, data),
      onMutate: async ({ kindSlug, ...data }) => {
        await queryClient.cancelQueries({
          queryKey: ['notifications-preferences', kindSlug],
        })
        const previous = queryClient.getQueryData<
          UserNotificationSettingSummary[]
        >(['notifications-preferences', kindSlug])

        queryClient.setQueryData<UserNotificationSettingSummary[]>(
          ['notifications-preferences', kindSlug],
          (old) =>
            old
              ? old.map((item) => ({
                  ...item,
                  email_enabled:
                    data.emailEnabled !== undefined
                      ? data.emailEnabled
                      : item.email_enabled,
                  in_app_enabled:
                    data.inAppEnabled !== undefined
                      ? data.inAppEnabled
                      : item.in_app_enabled,
                }))
              : old
        )

        return { previous }
      },

      onError: (_err, { kindSlug }, context) => {
        if (context?.previous) {
          queryClient.setQueryData(
            ['notifications-preferences', kindSlug],
            context.previous
          )
        }
      },

      onSettled: (_data, _error, { kindSlug }) => {
        queryClient.invalidateQueries({
          queryKey: ['notifications-preferences', kindSlug],
        })
      },
    })
  }

  async function updateNotificationsPreferences(
    preferenceSlug: string,
    data: {
      emailEnabled?: boolean
      inAppEnabled?: boolean
    }
  ) {
    return baseAPICall(() =>
      clientInstance.patch(`notifications/preferences/`, {
        preference: preferenceSlug,
        email_enabled: data.emailEnabled,
        in_app_enabled: data.inAppEnabled,
      })
    )
  }

  function useUpdateNotificationsPreferences() {
    return useMutation({
      mutationFn: ({
        preferenceSlug,
        data,
      }: {
        preferenceSlug: string
        kindSlug: string
        data: { emailEnabled?: boolean; inAppEnabled?: boolean }
      }) => updateNotificationsPreferences(preferenceSlug, data),

      onMutate: async ({ preferenceSlug, kindSlug, data }) => {
        await queryClient.cancelQueries({
          queryKey: ['notifications-preferences', kindSlug],
        })

        const previous = queryClient.getQueryData<
          UserNotificationSettingSummary[]
        >(['notifications-preferences', kindSlug])

        queryClient.setQueryData<UserNotificationSettingSummary[]>(
          ['notifications-preferences', kindSlug],
          (old) =>
            old
              ? old.map((oldItem) =>
                  oldItem.preference_slug === preferenceSlug
                    ? {
                        ...oldItem,
                        email_enabled:
                          data.emailEnabled !== undefined
                            ? data.emailEnabled
                            : oldItem.email_enabled,
                        in_app_enabled:
                          data.inAppEnabled !== undefined
                            ? data.inAppEnabled
                            : oldItem.in_app_enabled,
                      }
                    : oldItem
                )
              : old
        )

        return { previous, kindSlug }
      },

      onError: (_err, _vars, context: any) => {
        if (context?.previous) {
          queryClient.setQueryData(
            ['notifications-preferences', context.kindSlug],
            context.previous
          )
        }
      },

      onSettled: (_data, _error, { kindSlug }) => {
        queryClient.invalidateQueries({
          queryKey: ['notifications-preferences', kindSlug],
        })
      },
    })
  }

  async function mergeCompanies(
    originCompanyId: number,
    destinationChainId: number,
    destinationChainType: string
  ) {
    return baseAPICall<{ message: string }>(() =>
      clientInstance.post(`contacts/contactcompanies/merge/`, {
        origin_chain_proxy_id: originCompanyId,
        destination_chain_id: destinationChainId,
        destination_chain_type: destinationChainType,
      })
    )
  }

  async function mergeCustomCompanies(
    originCompanyId: number,
    destinationCompanyId: number
  ) {
    return baseAPICall<{ message: string }>(() =>
      clientInstance.post(`contacts/contactcompanies/merge/`, {
        origin_company_id: originCompanyId,
        destination_company_id: destinationCompanyId,
      })
    )
  }

  // Automations API functions
  async function getAutomations(params = {}, signal?: AbortSignal) {
    return baseAPICall<AutomationListResponse>(() =>
      clientInstance.get('automations/automations/', { params, signal })
    )
  }

  async function getAutomationById(id: number) {
    return baseAPICall<AutomationDetailResponse>(() =>
      clientInstance.get(`automations/automations/${id}/`)
    )
  }

  async function createAutomation(data: AutomationCreatePayload) {
    return baseAPICall<AutomationCreateResponse>(() =>
      clientInstance.post('automations/automations/', data)
    )
  }

  async function createCompanyNoteAutomation(data: AutomationMultipartPayload) {
    const formData = new FormData()

    // Add basic fields
    formData.append('name', data.name)
    if (data.description) {
      formData.append('description', data.description)
    }
    formData.append('is_active', String(data.is_active))

    // Add complex objects as JSON strings
    formData.append('condition_groups', JSON.stringify(data.condition_groups))
    formData.append('actions', JSON.stringify(data.actions))

    // Add file attachments
    if (data.attachments) {
      data.attachments.forEach((file: File) => {
        formData.append('attachments', file)
      })
    }

    return baseAPICall<AutomationCreateResponse>(() =>
      clientInstance.post('automations/automations/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    )
  }

  async function updateAutomation(id: number, data: AutomationCreatePayload) {
    return baseAPICall<AutomationCreateResponse>(() =>
      clientInstance.put(`automations/automations/${id}/`, data)
    )
  }

  async function updateCompanyNoteAutomation(
    id: number,
    data: AutomationMultipartPayload
  ) {
    const formData = new FormData()

    // Add basic fields
    formData.append('name', data.name)
    if (data.description) {
      formData.append('description', data.description)
    }
    formData.append('is_active', String(data.is_active))

    // Add complex objects as JSON strings
    formData.append('condition_groups', JSON.stringify(data.condition_groups))
    formData.append('actions', JSON.stringify(data.actions))

    // Add file attachments (only new files)
    if (data.attachments) {
      data.attachments.forEach((attachment) => {
        if (attachment instanceof File) {
          formData.append('attachments', attachment)
        }
        // Skip existing AutomationAttachment objects - they're already saved
      })
    }

    return baseAPICall<AutomationCreateResponse>(() =>
      clientInstance.put(`automations/automations/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    )
  }

  async function partialUpdateAutomation(
    id: number,
    data: Partial<AutomationCreatePayload>
  ) {
    return baseAPICall<AutomationCreateResponse>(() =>
      clientInstance.patch(`automations/automations/${id}/`, data)
    )
  }

  async function deleteAutomation(id: number) {
    return baseAPICall(() =>
      clientInstance.delete(`automations/automations/${id}/`)
    )
  }

  // Automation cycle validation functions
  async function validateAutomationCycles(companyId: number) {
    return baseAPICall<{
      company_id: number
      has_cycles: boolean
      cycle_count: number
      cycles: number[][]
      recommendations: string[]
      status: string
    }>(() =>
      clientInstance.get(`automations/validation/company/${companyId}/cycles/`)
    )
  }

  async function validateNewAutomation(
    companyId: number,
    automationData: {
      trigger_type: string
      action_type: string
      trigger_target_user_id?: number
      trigger_target_stage_id?: number
      target_user_id?: number
      target_stage_id?: number
      target_tag_id?: number
      target_campaign_id?: number
    }
  ) {
    return baseAPICall<{
      valid: boolean
      message: string
      can_create: boolean
    }>(() =>
      clientInstance.post(
        `automations/validation/company/${companyId}/validate-new/`,
        automationData
      )
    )
  }

  async function validateAutomationUpdate(
    companyId: number,
    automationId: number,
    automationData: {
      trigger_type: string
      action_type: string
      trigger_target_user_id?: number
      trigger_target_stage_id?: number
      target_user_id?: number
      target_stage_id?: number
      target_tag_id?: number
      target_campaign_id?: number
    }
  ) {
    return baseAPICall<{
      valid: boolean
      message: string
      can_update: boolean
    }>(() =>
      clientInstance.post(
        `automations/validation/company/${companyId}/automation/${automationId}/validate-update/`,
        automationData
      )
    )
  }

  function updateReportingUnit(args: UpdateReportingUnitRequest) {
    return baseAPICall(() => {
      return clientInstance.post('preferences/reporting-unit/update/', {
        reporting_unit: args.reporting_unit,
      })
    })
  }

  const useUpdateReportingUnit = () => {
    return useMutation({
      mutationFn: (variables: UpdateReportingUnitRequest) =>
        updateReportingUnit(variables),
      onMutate: async (newValue) => {
        const prev = queryClient.getQueriesData({
          queryKey: ['preferences'],
        })

        queryClient.setQueriesData(
          { queryKey: ['preferences'] },
          (old?: Preferences) => {
            if (!old) {
              return
            }
            return {
              ...old,
              reporting_unit: newValue.reporting_unit,
            }
          }
        )

        return { prev }
      },
      onError: (_, __, context) => {
        queryClient.setQueryData(['preferences'], context?.prev)
        queryClient.setQueriesData(
          {
            queryKey: ['preferences'],
          },
          context?.prev
        )
      },
    })
  }

  return {
    postProduct,
    putProduct,
    deleteProduct,
    getRestaurantsV2,
    getPlaceV2,
    useGetPlaceV2,
    getChain,
    getUsers,
    getUserTypes,
    updateUserType,
    getRSMEligibleUsers,
    useGetUsers,
    bulkEditCompanyAttrs,
    getSalesPipeline,
    getSalesStages,
    getTags,
    postSalesStage,
    postTag,
    putTag,
    putStage,
    deleteStage,
    bulkReorderSalesStages,
    deleteTag,
    useGetSalesStages,
    useGetTags,
    getChainsListV2,
    useGetProducts,
    useGetTagsOptions,
    useGetUserOptions,
    useGetSalesStageOptions,
    useGetPlacesSummaryV2,
    useGetChainsSummaryV2,
    getContactsList,
    getContactsCounts,
    useGetContactsList,
    useGetContactsInfinite,
    useGetContactRequestsInfinite,
    useGetContactsCounts,
    getProducts,
    searchCompaniesList,
    createContact,
    deleteContact,
    useGetContactDetails,
    updateContact,
    previewContactsImport,
    contactsImport,
    useGetCompanyCategories,
    useGetActiveCompanyCategoriesAndTypes,
    getCompaniesList,
    getCompaniesSummary,
    getCompany,
    useGetCompany,
    resolveCompanyFromFbIds,
    createCompany,
    updateCompany,
    useGetCompanyDistributorsOptions,
    useGetCompanyGPOsOptions,
    useGetUniversityFoodManagementCompanyOptions,
    getUniversitySummary,
    useGetProductPredictions,
    getDeals,
    useGetDeals,
    useGetDealsInfinite,
    useGetDealsCounts,
    commonCreateDeals,
    bulkEditDeal,
    bulkEditDealReports,
    deleteDeal,
    manageDealRelatedCompanies,
    useProducts,
    editDeal,
    deleteCompany,
    useGetProductsOptions,
    useGetProductVariantsOptions,
    useGetDealHistory,
    getDeal,
    useGetDeal,
    getDealsPipeline,
    useGetPreferences,
    getActivityList,
    getMyActivityList,
    getNotes,
    editNote,
    deleteNote,
    createNote,
    getDealNotes,
    createDealNote,
    editDealNote,
    deleteDealNote,
    useGetChain,
    useGetUnreadNotificationsCount,
    useGetNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    useGetDashboardOverview,
    saveFilters,
    useGetSavedFiltersOptions,
    useGetDashboardSalesForecast,
    getChainMenuList,
    useGetChainMenuList,
    useGetUniversity,
    getUniversitiesList,
    postSuggestion,
    getDoorsMenuList,
    useGetDoorsMenuList,
    getActivityListV2,
    getCompanyActivityList,
    getDealActivityList,
    postDistributor,
    updateDistributor,
    getDistributors,
    deleteDistributor,
    postGPO,
    updateGPO,
    deleteGPO,
    getGPOs,
    postCampaign,
    useGetGPOs,
    useDoorProxiesOfChainProxy,
    createDoorProxy,
    deleteDoorProxy,
    editDoorProxy,
    getK12DistrictsList,
    useGetK12District,
    getK12DistrictsSummary,
    useK12CountiesOptions,
    searchCompaniesUniqueDomain,
    getProductTagsList,
    getContactTagsList,
    getDocumentTagsList,
    getDealTagsList,
    getMyActivityListV2,
    useGetUnmatchedCompanyCities,
    useGetDoorProxyCities,
    useGetDoorProxyStates,
    useGetDistributors,
    createContactRequestJob,
    fetchContactRequestJobs,
    fetchContactRequestJobDetails,
    useChangeMiscSettings,
    deleteNoteAttachment,
    addAttachmentsToNote,
    useGetAllSavedFilters,
    useGetCampaigns,
    useEditCampaign,
    useCreateCampaign,
    useCreateCampaignV2,
    useDeleteCampaign,
    useAssignToCampaignMutation,
    useUnassignToCampaignMutation,
    deleteFilter,
    updateFilter,
    useGetAsyncJobs,
    fetchRequestedContacts,
    useRequestContactsFromCampaign,
    useGetCampaignsOptions,
    useGetHospital,
    getHospitalsList,
    getNursingHome,
    getNursingHomesList,
    getNursingHomesSummary,
    useGetNursingHome,
    deleteColumnPreset,
    createColumnPreset,
    useGetAllColumnPresets,
    useGetColumnPresetOptions,
    contactStarToggle,
    useGetRebateOffer,
    getRebateOffers,
    createRebateOffer,
    useGetRebateClaims,
    useGetRebateEnrollments,
    useGetRebateEnrollmentDetails,
    useGetOfferUserDetails,
    useManufacturerInvoices,
    useGetCurrentManufacturerInvoice,
    useGetRebateOfferOptions,
    getRebateDashboardStats,
    useGetRebateDashboardStats,
    bulkDeleteDeals,
    bulkDeleteDealReports,
    inviteUser,
    checkInviteToken,
    listInvitations,
    resendInvitation,
    useGetCampaignCompanies,
    getStripePortalRedirect,
    getLodgingList,
    getLodgingDoorList,
    useGetLodging,
    useGetLodgingDoor,
    getLodgingSummary,
    getLodgingDoorSummary,
    getConvenienceList,
    getConvenienceDoorList,
    useGetConvenience,
    useGetConvenienceDoor,
    getConvenienceSummary,
    getConvenienceDoorSummary,
    getCampaigns,
    getHospitalsSummary,
    emailIntegration: {
      getEmailIntegrationList,
      useGetEmailIntegrationOptions,
      getEmailIntegrationAuthUrl,
      reauthenticateEmailIntegration,
      updateEmailIntegration,
      deleteEmailIntegration,
      getEmailMessageList,
      syncEmailIntegration,
      sendEmailMessage,
      getEmailMessageById,
      getEmailThreadList,
      getThreadMessages,
      getDistinctSenders,
      getDistinctRecipients,
      assignThreadsToDeal,
      removeThreadFromDeal,
      hideThread,
      unhideThread,
      searchDeals,
    },
    grants: {
      getGrantsList,
      deleteGrant,
      updateGrantPreferences,
    },
    calendars: {
      getCalendars,
      selectCalendar,
    },
    commonManageTags,
    commonManageDealTags,
    commonManageDistributors,
    commonManageGPOs,
    useGetTextContent,
    getAttributionCodes,
    useGetAttributionCodes,
    createAttributionCode,
    updateAttributionCode,
    deleteAttributionCode,
    dataRequestSubmission,
    optOutSubmission,
    fetchCreditBalance,
    purchaseCredits,
    createCreditCheckoutSession,
    getRebateEnrollments,
    commonExportTable,
    useCommonGetCityStates,
    useCommonGetCityStatesInfinite,
    getCompanyDetails,
    updateCompanyDetails,
    getDoorsForSelection: getDoorsForDealSelection,
    getDoorsForDealSelectionIdsOnly: getDoorsForSelectionIdsOnly,
    useGetLocationsInfinite,
    getDoorsForSelectionCount,
    useGetDoorsForSelectionCount,
    getSelectedDoorIdsForDeal,
    useGetSelectedDoorIdsForDeal,
    useGetCompanyDetails,
    postCompanyDomain,
    cancelCompanyDomainRequest,
    useUpdateReportingUnit,
    // Company Logo Management
    getCompanyLogos,
    createCompanyLogo,
    getCompanyLogo,
    updateCompanyLogo,
    deleteCompanyLogo,
    setCompanyLogoPrimary,
    useGetCompanyLogos,
    useGetCompanyLogo,
    useCreateCompanyLogo,
    useUpdateCompanyLogo,
    useDeleteCompanyLogo,
    useSetCompanyLogoPrimary,
    getDoorProxy,
    useGetDoorProxy,
    useGetCompanyDomainRequests,
    useGetCompanyEmailExtensions,
    getAllDoorProxies,
    getAllDoorProxiesSummary,
    bulkEditDoorAttrs,
    getDoorProxiesOfChainProxySummary,
    getDoorProxiesOfChainProxy,
    getNotificationsPreferencesByKind,
    useGetNotificationsPreferencesByKind,
    updateAllNotificationsPreferencesByKind,
    useUpdateAllNotificationsPreferencesByKind,
    updateNotificationsPreferences,
    useUpdateNotificationsPreferences,
    mergeCompanies,
    mergeCustomCompanies,
    searchFirstBiteCompanies,
    searchUnmatchedCompanies,
    searchFirstBiteDoors,
    searchUnmatchedDoors,
    // Automations
    getAutomations,
    getAutomationById,
    createAutomation,
    updateAutomation,
    partialUpdateAutomation,
    deleteAutomation,
    createCompanyNoteAutomation,
    updateCompanyNoteAutomation,
    deleteAutomationAttachment,
    // Automation cycle validation
    validateAutomationCycles,
    validateNewAutomation,
    validateAutomationUpdate,
    // Reminders
    getReminders,
    createReminder,
    getReminder,
    updateReminder,
    deleteReminder,
    pauseReminder,
    unpauseReminder,
    cancelReminder,
    // Reports
    getDoorsWithCRMReports,
    getChainsWithCRMReports,
    useGetChainsWithCRMReports,
    getReportDeals,
    useGetReportDeals,
    useGetReportDealsInfinite,
    useGetChainReportsSummary,
    getChainReportsSummary,
    useGetDoorReportsSummary,
    getDoorReportsSummary,
    // Dashboard V2
    getSalesPipelineV2,
    getDealsPipelineV2,
  }
}

// Export the return type of apiService for use in other modules
export type ApiService = ReturnType<typeof useApiService>
